import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { API, fileUrl, formatBRL, formatApiError, formatDateBR } from "../lib/api";
import { getClientIp } from "../lib/clientIp";
import { computeListingDiscount, describeListingPromos, computeCyclePricing, cycleBreakdownLabel, describeFrete, unitLabelForItem, getListingPriceRange } from "../lib/pricing";
import { useAuth } from "../lib/auth";
import { useAuthModal } from "../lib/authModal";
import { useAppConfig } from "../lib/appConfig";
import { MapPin, Star, Calendar, User, Truck, Package, House, PencilSimple, CreditCard, QrCode, Shield, Warning, Clock, Wallet, X, Copy, CheckCircle, Trash, Confetti, Lightning, ShareNetwork, ArrowLeft, Plus, Calculator, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { shareContent } from "../lib/native";
import AsaasCardForm from "../components/AsaasCardForm";
import PaymentProgress from "../components/PaymentProgress";
import ImageLightbox from "../components/ImageLightbox";
import useBodyScrollLock from "../lib/useBodyScrollLock";
import { Calendar as ShadcnCalendar } from "../components/ui/calendar";
import { ptBR } from "date-fns/locale";

export default function ListingDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const { requireAuth } = useAuthModal();
  const cfg = useAppConfig();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // V17.1: lightbox único para foto de complemento (variante/opcional/consumível)
  const [compLightboxImg, setCompLightboxImg] = useState(null);
  // V7.6: Retomar checkout — pré-popula campos via query params
  const [start, setStart] = useState(params.get("start") || "");
  const [end, setEnd] = useState(params.get("end") || "");
  const [deliveryChoice, setDeliveryChoice] = useState(params.get("delivery") || "");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  // Epic 1: modo de locação por ciclo (flexible/closed)
  const [blocks, setBlocks] = useState(1);
  // Epic 2: endereço de entrega + frete dinâmico
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(params.get("selectedAddressId") || "");
  const [ownerSchedule, setOwnerSchedule] = useState(null);
  const [fretePreview, setFretePreview] = useState(null); // {dias, timezone, tz_label, configured}
  const [paymentMethod, setPaymentMethod] = useState("card");  // card | pix | wallet
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLocked, setWalletLocked] = useState(0);
  // V9.8: split (Saldo + Gateway) — quando o saldo não cobre o total
  const [splitGateway, setSplitGateway] = useState(null);  // null | "card" | "pix"
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  // V7.6: nota livre do locatário para o locador (ex: "Entregar para o vizinho")
  const [deliveryNotes, setDeliveryNotes] = useState("");
  // V7.2
  const [quantity, setQuantity] = useState(parseInt(params.get("qty") || "1", 10) || 1);
  const [stockInfo, setStockInfo] = useState(null); // {disponivel, estoque_total}
  // V17: complementos escolhidos + calculadora de conversão
  const [selVariant, setSelVariant] = useState({});   // {complementIndex: optionIndex}
  // V18.3 — controle de expansão da descrição opcional da variante (colapsada por padrão)
  const [variantDescOpen, setVariantDescOpen] = useState({}); // { "compIdx:optIdx": true }
  // V18.8 — controle de expansão da descrição opcional de addons/consumables (colapsada por padrão)
  const [addonDescOpen, setAddonDescOpen] = useState({}); // { compIdx: true }
  const [selAddons, setSelAddons] = useState({});     // {complementIndex: qty}
  // V17.10 — Ordem de escolha por grupo, para regra "max": FIFO — quando estoura o limite, o mais antigo sai.
  const groupOrderRef = useRef({}); // { [group_key]: [complementIndex, ...] }
  const [convInput, setConvInput] = useState("");
  const [cutoffInfo, setCutoffInfo] = useState(null); // {ok, blocked_reason, eta_iso}
  const [blockedDates, setBlockedDates] = useState([]); // V8.5: datas lotadas (YYYY-MM-DD)
  const isResume = !!params.get("resume");
  // V17.2: kit de combos — fluxo "alugar tudo junto"
  // 1) na página do item mãe o cliente marca combos, clica "Alugar tudo"
  // 2) após pagar o principal, salva `kit_queue` no sessionStorage e redireciona pro 1º combo
  // 3) no combo, pré-preenche datas + envia combo_source_listing_id no checkout (aplica desconto)
  const [comboSelected, setComboSelected] = useState({}); // {listing_id: true}
  const comboFrom = params.get("combo_from") || null;
  const kitPos = params.get("kit_pos") ? parseInt(params.get("kit_pos"), 10) : 0;
  const kitTotal = params.get("kit_total") ? parseInt(params.get("kit_total"), 10) : 0;
  // V17.9.3 — modo simulador (dono vendo como o cliente veria, sem checkout real)
  const simulateMode = params.get("simulate") === "1";
  const [simulateResult, setSimulateResult] = useState(null);

  const [notFound, setNotFound] = useState(false);
  // V18.0 — Kit unificado: preview do total agregado (principal + combos + adicionais)
  const [kitQuote, setKitQuote] = useState(null); // { kit_valor_diarias, combos: [], total_charged, ... }
  const [kitQuoteLoading, setKitQuoteLoading] = useState(false);
  // V8.1: modais de cartão + QR PIX in-app
  const [showCardForm, setShowCardForm] = useState(false);
  // V11.6: cartão salvo (token Asaas) — pagar com 1 toque ou usar outro cartão
  const [useOtherCard, setUseOtherCard] = useState(false);
  const [cardError, setCardError] = useState("");
  const [cardSuccess, setCardSuccess] = useState(false);
  useBodyScrollLock(showCardForm);
  const [pixCheckout, setPixCheckout] = useState(null);  // {qr_code, qr_code_base64, total_charged, ...}
  const [currentBookingId, setCurrentBookingId] = useState(null);
  const [pixPolls, setPixPolls] = useState(0);

  // V8.1: polling PIX status pós-checkout (a cada 3s, até 30 tentativas = 90s)
  useEffect(() => {
    if (!pixCheckout || !currentBookingId) return;
    if (pixPolls >= 30) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/bookings/status/${currentBookingId.toString().startsWith("wallet_") ? currentBookingId : (pixCheckout.asaas_payment_id || currentBookingId)}`);
        if (data.payment_status === "paid") {
          toast.success("Pagamento PIX confirmado!");
          nav(`/reservas/sucesso?booking_id=${currentBookingId}`);
          return;
        }
        setPixPolls((p) => p + 1);
      } catch { /* keep */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [pixCheckout, currentBookingId, pixPolls, nav]);

  const copyPixCode = () => {
    if (!pixCheckout?.qr_code) return;
    navigator.clipboard.writeText(pixCheckout.qr_code);
    toast.success("Código PIX copiado!");
  };

  useEffect(() => {
    setNotFound(false);
    // V17.9.4 — modo preview a partir do formulário (cadastro): lê listing do sessionStorage
    if (id === "preview" && simulateMode) {
      try {
        const raw = sessionStorage.getItem("aj_listing_preview");
        if (raw) {
          const data = JSON.parse(raw);
          setItem(data);
          const preset = {};
          (data.complements || []).forEach((c, i) => {
            if ((c.kind || "").toLowerCase() === "variant") {
              const defIdx = (c.options || []).findIndex((o) => !!o.is_default);
              if (defIdx >= 0) preset[i] = defIdx;
              else if (c.required && (c.options || []).length > 0) preset[i] = 0;
            }
          });
          if (Object.keys(preset).length) setSelVariant((s) => ({ ...preset, ...s }));
          setReviews([]);
          return;
        }
      } catch { /* ignore */ }
      setItem(null);
      setNotFound(true);
      return;
    }
    api.get(`/listings/${id}`).then((r) => {
      setItem(r.data);
      // V17.2: pré-seleciona a opção padrão de cada variante (marca de "padrão" no cadastro)
      const preset = {};
      (r.data.complements || []).forEach((c, i) => {
        if ((c.kind || "").toLowerCase() === "variant") {
          const defIdx = (c.options || []).findIndex((o) => !!o.is_default);
          if (defIdx >= 0) preset[i] = defIdx;
          else if (c.required && (c.options || []).length > 0) preset[i] = 0;
        }
      });
      if (Object.keys(preset).length) setSelVariant((s) => ({ ...preset, ...s }));
      // Pre-fill delivery choice if listing has only one option (a menos que já veio do resume)
      if (!params.get("delivery")) {
        if (r.data.regras_entrega === "eu_entrego") setDeliveryChoice("eu_entrego");
        else if (r.data.regras_entrega === "locatario_busca") setDeliveryChoice("locatario_busca");
      }
    }).catch((e) => {
      if (e?.response?.status === 404) setNotFound(true);
    });
    api.get(`/reviews/${id}`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) {
      api.get(`/reviews/${id}/can-review`).then((r) => setCanReview(r.data.can_review)).catch(() => setCanReview(false));
      // V7.4: carrega saldo da wallet para mostrar opção de pagar com saldo.
      // V15.3: usa o DISPONÍVEL (available), não o total — valores a liberar (30d) e reservas
      // não podem ser gastos; senão a UI mostra saldo que o pagamento rejeita.
      api.get("/wallet/me").then((r) => {
        setWalletBalance(r.data?.available || 0);
        setWalletLocked(r.data?.locked || 0);
      }).catch(() => setWalletBalance(0));
      // Epic 2: endereços salvos (destino de entrega p/ o frete dinâmico)
      api.get("/addresses").then((r) => {
        setAddresses(r.data || []);
        setSelectedAddressId((cur) => {
          if (cur && (r.data || []).some((a) => a.id === cur)) return cur;
          const def = (r.data || []).find((a) => a.is_default) || (r.data || [])[0];
          return def?.id || "";
        });
      }).catch(() => setAddresses([]));
    } else {
      setWalletBalance(null);
    }
  }, [user, id]);

  // V7.2: Re-checa disponibilidade quando datas/quantidade mudam
  useEffect(() => {
    if (!item || !start || !end) { setStockInfo(null); return; }
    if (id === "preview") { setStockInfo({ disponivel: item.quantidade_total || 1 }); return; }
    const ctrl = new AbortController();
    api.get(`/listings/${id}/availability`, {
      params: { start_date: start, end_date: end, quantity },
      signal: ctrl.signal,
    }).then((r) => setStockInfo(r.data)).catch(() => {});
    return () => ctrl.abort();
  }, [id, item, start, end, quantity]);

  // V7.2: Cutoff de mesmo dia
  useEffect(() => {
    if (!item || !deliveryChoice || !start) { setCutoffInfo(null); return; }
    if (id === "preview") { setCutoffInfo(null); return; }
    const today = new Date().toISOString().split("T")[0];
    if (start !== today) { setCutoffInfo(null); return; }
    api.get(`/listings/${id}/cutoff-check`, {
      params: { delivery_choice: deliveryChoice, date: start },
    }).then((r) => setCutoffInfo(r.data)).catch(() => {});
  }, [id, item, deliveryChoice, start]);

  // V8.5: Datas lotadas — busca uma única vez por anúncio
  useEffect(() => {
    if (!item || !id) return;
    if (id === "preview") { setBlockedDates([]); return; }
    api.get(`/listings/${id}/blocked-dates`, { params: { days_ahead: 90 } })
      .then((r) => setBlockedDates(r.data?.blocked_dates || []))
      .catch(() => setBlockedDates([]));
  }, [id, item]);

  // V11.0: horário de atendimento do dono (para limitar retirada/devolução)
  useEffect(() => {
    if (!id) return;
    if (id === "preview") { setOwnerSchedule(null); return; }
    api.get(`/listings/${id}/schedule`).then((r) => setOwnerSchedule(r.data)).catch(() => setOwnerSchedule(null));
  }, [id]);
  // Epic 2: estimativa de frete p/ o endereço padrão do locatário (mostra "Frete grátis"/valor antes do checkout)
  useEffect(() => {
    if (!id || !item || !user) { setFretePreview(null); return; }
    if (id === "preview") { setFretePreview(null); return; }
    const rg = item.regras_entrega;
    if (rg !== "eu_entrego" && rg !== "ambos") { setFretePreview(null); return; }
    api.get(`/listings/${id}/frete-preview`)
      .then((r) => setFretePreview(r.data))
      .catch(() => setFretePreview(null));
  }, [id, item, user]);
  // ao trocar a data, limpa o horário (força re-escolha dentro do dia)
  useEffect(() => { setStartTime(""); }, [start]);

  // V11.5 (item 3): trava o scroll do fundo enquanto um modal de pagamento está aberto —
  // evita o glitch no mobile (o modal "deforma" e o X de fechar some/trava ao rolar a página).
  useEffect(() => {
    const open = !!pixCheckout || !!showCardForm;
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [pixCheckout, showCardForm]);
  useEffect(() => { setEndTime(""); endManualRef.current = false; }, [end]);

  // V12.7: devolução = retirada automaticamente (horário igual ou o mais próximo disponível).
  // Mantém a lista e a troca manual; se o usuário mudar a devolução, paramos de sincronizar.
  const endManualRef = useRef(false);
  useEffect(() => {
    if (!startTime || !end || endManualRef.current) return;
    const opts = timeOptionsFor(end, "pickup");
    if (!opts.length) return;
    if (opts.includes(startTime)) { setEndTime(startTime); return; }
    const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
    const target = toMin(startTime);
    const closest = opts.reduce((best, o) =>
      Math.abs(toMin(o) - target) < Math.abs(toMin(best) - target) ? o : best, opts[0]);
    setEndTime(closest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, end, ownerSchedule]);

  // V7.2/V7.8: minDate dinâmico — bloqueia "hoje" se cutoff já passou. Usa cutoff DO ANÚNCIO se definido.
  // V18.2 — Fix bug do "cutoff já passou às 8h da manhã": em vez de usar `cfg.server_now_brasilia`
  // (que é carregado UMA VEZ no boot e fica estagnado se o app ficar em background),
  // computamos a hora ATUAL no fuso do anúncio direto no cliente a cada segundo.
  const todayISO = new Date().toISOString().split("T")[0];
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // Re-avalia o cutoff a cada 60s + quando a janela volta ao foco (background→foreground).
    const iv = setInterval(() => setTick((t) => t + 1), 60_000);
    const onFocus = () => setTick((t) => t + 1);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, []);
  const cutoffsExpired = useMemo(() => {
    const cutoffEntrega = item?.cutoff_entrega || cfg.horario_limite_entrega || "14:00";
    const cutoffRetirada = item?.cutoff_retirada || cfg.horario_limite_retirada || "17:00";
    // Hora ATUAL no fuso do anúncio (default America/Sao_Paulo) — computed a cada tick
    const tz = item?.timezone || "America/Sao_Paulo";
    let nowHours = 0, nowMinutes = 0;
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz, hour12: false, hour: "2-digit", minute: "2-digit",
      }).formatToParts(new Date());
      const hh = parts.find((p) => p.type === "hour")?.value;
      const mm = parts.find((p) => p.type === "minute")?.value;
      nowHours = parseInt(hh, 10);
      nowMinutes = parseInt(mm, 10);
      if (nowHours === 24) nowHours = 0; // Intl edge case
    } catch {
      const d = new Date();
      nowHours = d.getHours(); nowMinutes = d.getMinutes();
    }
    const [hE, mE] = cutoffEntrega.split(":").map(Number);
    const [hR, mR] = cutoffRetirada.split(":").map(Number);
    return {
      entrega: nowHours > hE || (nowHours === hE && nowMinutes >= mE),
      retirada: nowHours > hR || (nowHours === hR && nowMinutes >= mR),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, cfg.horario_limite_entrega, cfg.horario_limite_retirada, item?.cutoff_entrega, item?.cutoff_retirada, item?.timezone]);
  // Se a delivery escolhida tem cutoff expirado, "hoje" não é permitido como data inicial
  const effectiveDelivery = item?.regras_entrega === "ambos" ? deliveryChoice : item?.regras_entrega;
  const todayBlocked = (
    effectiveDelivery === "eu_entrego" ? cutoffsExpired.entrega
    : effectiveDelivery === "locatario_busca" ? cutoffsExpired.retirada
    : false
  );
  // V7.8: auto-ajusta data se cair em dia não-operacional do locador
  // Estratégia: se end_date cair em dia não-operacional, empurra para o próximo dia operacional
  // (locação custa apenas os dias usados, mas devolução tem que ser em dia válido)
  const adjustToOperationalDay = (dateIso, direction = "forward") => {
    if (!dateIso) return dateIso;
    if (!Array.isArray(item?.dias_operacao) || item.dias_operacao.length === 0 || item.dias_operacao.length >= 7) {
      return dateIso;
    }
    const dias = new Set(item.dias_operacao);
    const d = new Date(dateIso + "T12:00:00");
    let attempts = 0;
    while (!dias.has(d.getDay()) && attempts < 14) {
      d.setDate(d.getDate() + (direction === "back" ? -1 : 1));
      attempts++;
    }
    return d.toISOString().split("T")[0];
  };

  // V11.4 (item 2): ajuste pelo horário de atendimento do DONO (Perfil).
  // Se o dia escolhido for um dia que o dono NÃO atende, ajusta automaticamente:
  // retirada → dia anterior atendido; devolução → próximo dia atendido (com aviso).
  const ownerClosedOn = (iso) => {
    if (!iso || !ownerSchedule?.dias) return false;
    const dow = new Date(`${iso}T12:00:00`).getDay();
    return !ownerSchedule.dias[String(dow)]?.aberto;
  };
  const adjustToOwnerDay = (iso, direction) => {
    if (!iso || !ownerSchedule?.dias) return iso;
    const d = new Date(`${iso}T12:00:00`);
    let attempts = 0;
    while (ownerClosedOn(d.toISOString().split("T")[0]) && attempts < 14) {
      d.setDate(d.getDate() + (direction === "back" ? -1 : 1));
      attempts++;
    }
    return d.toISOString().split("T")[0];
  };

  const onChangeStart = (val) => {
    if (!val) { setStart(""); return null; }
    // V8.5: bloqueia datas lotadas
    if (blockedDates.includes(val)) {
      toast.error("Esta data já está reservada por outro locatário. Escolha outra data.");
      return null;
    }
    // V7.8: empurra para amanhã se "hoje" estiver bloqueado por cutoff (mobile webview pode ignorar `min`)
    let candidate = val;
    if (candidate === todayISO && todayBlocked) {
      candidate = new Date(Date.now() + 86400000).toISOString().split("T")[0];
      toast.info("Hoje não está mais disponível — início ajustado para amanhã.");
    }
    const adjusted = adjustToOperationalDay(candidate, "forward");
    // V11.4 (item 2): dono fechado → retirada vai para o dia ANTERIOR atendido
    let finalStart = adjustToOwnerDay(adjusted, "back");
    if (finalStart < minStartDate) finalStart = adjustToOwnerDay(adjusted, "forward");
    if (finalStart !== candidate) {
      toast.info(`O dono não atende nesse dia — retirada ajustada para ${formatDateBR(finalStart) || finalStart}.`);
    }
    setStart(finalStart);
    // Se end_date < new start, limpa
    if (end && end < finalStart) setEnd("");
    return finalStart;
  };

  const onChangeEnd = (val) => {
    if (!val) { setEnd(""); return; }
    // V8.5: bloqueia data final lotada
    if (blockedDates.includes(val)) {
      toast.error("Esta data já está reservada por outro locatário. Escolha outra data.");
      return;
    }
    // V8.5: bloqueia se há datas lotadas entre start e val
    if (start && val > start) {
      const blockedInRange = blockedDates.filter((d) => d > start && d <= val);
      if (blockedInRange.length > 0) {
        toast.error(`Há ${blockedInRange.length} data(s) reservada(s) entre ${formatDateBR(start)} e ${formatDateBR(val)}. Escolha outro período.`);
        return;
      }
    }
    const adjusted = adjustToOperationalDay(val, "forward");
    // V11.4 (item 2): dono fechado → devolução vai para o PRÓXIMO dia atendido
    const finalEnd = adjustToOwnerDay(adjusted, "forward");
    if (finalEnd !== val) {
      toast.info(`O dono não atende nesse dia — devolução ajustada para ${formatDateBR(finalEnd) || finalEnd}.`);
    }
    setEnd(finalEnd);
  };

  const minStartDate = todayBlocked
    ? new Date(Date.now() + 86400000).toISOString().split("T")[0]
    : todayISO;

  // Epic 1: config de ciclo do anúncio
  const CYCLE_DAYS = { day: 1, week: 7, fortnight: 15, month: 30 };
  const rentalMode = item?.rental_mode || "legacy";
  const isClosed = rentalMode === "closed";
  const isFlexible = rentalMode === "flexible";
  const cycleDays = CYCLE_DAYS[item?.cycle_unit] || (item?.tipo_cobranca === "MENSAL" ? 30 : 1);
  const minCycles = Math.max(1, item?.min_cycles || 1);
  const minDaysFlex = minCycles * cycleDays;
  const addDaysISO = (iso, n) => {
    const d = new Date(iso + "T12:00:00");
    d.setDate(d.getDate() + n);
    return d.toISOString().split("T")[0];
  };
  // Fechado: quando muda o início ou o nº de blocos, recalcula a data-fim (trava)
  useEffect(() => {
    if (!isClosed || !start) return;
    const rawEnd = addDaysISO(start, blocks * cycleDays - 1);
    const finalEnd = adjustToOwnerDay(adjustToOperationalDay(rawEnd, "forward"), "forward");
    if (finalEnd !== end) setEnd(finalEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosed, start, blocks, cycleDays]);

  // Seleção determinística de período — "borda mais próxima vence".
  // Clique <= início → move o início; clique >= fim → estende; clique dentro → ajusta a borda
  // mais próxima (início ou fim). Isso permite reduzir/aumentar a data-fim sem resetar o início.
  const handleDayPick = (iso) => {
    if (blockedDates.includes(iso)) {
      toast.error("Esta data já está reservada por outro locatário. Escolha outra data.");
      return;
    }
    if (isClosed) { onChangeStart(iso); return; } // o useEffect deriva a data-fim
    // V17.9.7 — Legacy MENSAL / cycleDays > 1 (ex: "por mês" no modo padrão):
    // ao clicar um dia, o calendário deve auto-preencher o ciclo inteiro (30 dias)
    // — antes o minSpan era 1 e o cliente conseguia escolher só 1 dia com preço mensal.
    const isCycleBasedLegacy = !isClosed && !isFlexible && cycleDays > 1;
    const minSpan = isFlexible ? minDaysFlex : (isCycleBasedLegacy ? cycleDays : 1);
    // V17.9.7 — label amigável do mínimo (ex: "1 mês" em vez de "30 dia(s)")
    const cycleLabel = ({ 30: "mês", 15: "quinzena", 7: "semana" })[cycleDays];
    const minLabel = cycleLabel ? `1 ${cycleLabel} (${minSpan} dias)` : `${minSpan} dia(s)`;
    const daysBetween = (a, b) => Math.round((new Date(b + "T12:00:00") - new Date(a + "T12:00:00")) / 86400000);
    const freshStart = (s) => {
      const fs = onChangeStart(s);
      if (!fs) return;
      if (minSpan > 1) {
        const adjEnd = adjustToOwnerDay(adjustToOperationalDay(addDaysISO(fs, minSpan - 1), "forward"), "forward");
        setEnd(adjEnd);
        if (fs === s) toast.info(`Período mínimo de ${minLabel} — devolução em ${formatDateBR(adjEnd) || adjEnd}.`);
      } else {
        setEnd(fs);
      }
    };
    // Define a data-fim respeitando o mínimo e checando dias reservados no meio.
    const setEndChecked = (endIso) => {
      const blockedInRange = blockedDates.filter((d) => d > start && d <= endIso);
      if (blockedInRange.length > 0) {
        toast.error(`Há ${blockedInRange.length} dia(s) reservado(s) no período. Reiniciei no dia que você clicou.`);
        freshStart(endIso);
        return;
      }
      const span = daysBetween(start, endIso) + 1;
      if (span < minSpan) {
        const adjEnd = adjustToOwnerDay(adjustToOperationalDay(addDaysISO(start, minSpan - 1), "forward"), "forward");
        setEnd(adjEnd);
        toast.info(`Período mínimo de ${minLabel} — devolução em ${formatDateBR(adjEnd) || adjEnd}.`);
        return;
      }
      onChangeEnd(endIso);
    };
    if (!start) { freshStart(iso); return; }
    if (iso <= start) { freshStart(iso); return; }   // clicou no início ou antes → novo início
    if (!end || iso >= end) { setEndChecked(iso); return; } // depois do fim → estende
    // Clique DENTRO do período → ajusta a borda mais próxima
    if (daysBetween(start, iso) <= daysBetween(iso, end)) freshStart(iso);
    else setEndChecked(iso);
  };

  // V17.2+: hooks que precisam rodar SEMPRE (mesmo antes do item carregar).
  // V17.5: disponibilidade de estoque por complemento (recomputa quando as datas mudam)
  const [compAvail, setCompAvail] = useState({}); // { [compIndex]: {available, paused, unlimited} }
  const [listingBlockedBy, setListingBlockedBy] = useState(null); // V17.5+: nome do addon obrigatório que bloqueia o item
  useEffect(() => {
    if (!item?.id) { setCompAvail({}); setListingBlockedBy(null); return; }
    const hasAddonOrConsumable = (item.complements || []).some((c) => c.kind === "addon" || c.kind === "consumable");
    if (!hasAddonOrConsumable) return;
    let ignore = false;
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (start) qs.set("start", start);
        if (end) qs.set("end", end);
        const r = await api.get(`/listings/${item.id}/complements-availability?${qs.toString()}`);
        if (ignore) return;
        const map = {};
        for (const row of (r.data?.items || [])) {
          map[row.complement_index] = { available: row.available, paused: row.paused, unlimited: row.unlimited, stock: row.stock, reserved: row.reserved };
        }
        setCompAvail(map);
        setListingBlockedBy(r.data?.listing_blocked ? r.data.listing_blocked_by : null);
      } catch { /* opcional: não bloqueia a página */ }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, start, end]);
  // Auto-eleva a quantidade dos addons obrigatórios (fixo ou proporcional).
  useEffect(() => {
    const comps = item?.complements || [];
    if (!comps.length) return;
    const updates = {};
    comps.forEach((c, i) => {
      if (c.kind !== "addon" || !c.require_active) return;
      const maxq = Math.max(1, parseInt(c.max_qty || 1, 10) || 1);
      const mode = c.require_mode || "proportional";
      let min = 0;
      if (mode === "fixed") {
        min = Math.min(maxq, Math.max(1, parseInt(c.require_min_fixed || 1, 10) || 1));
      } else if (mode === "proportional") {
        const per = Math.max(1, parseInt(c.require_per_base_qty || 1, 10) || 1);
        const ratio = Math.max(1, parseInt(c.require_ratio || 1, 10) || 1);
        const src = c.require_per_source || "quantity";
        const baseVal = src === "measure" ? (parseFloat(convInput) || 0) : (parseInt(quantity || 1, 10) || 1);
        if (baseVal <= 0) return;
        min = Math.min(maxq, Math.floor(baseVal / per) * ratio);
      }
      if (min <= 0) return;
      const cur = parseInt(selAddons[i] || 0, 10) || 0;
      if (cur < min) updates[i] = min;
    });
    if (Object.keys(updates).length) setSelAddons((m) => ({ ...m, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, convInput, item?.id]);
  // V17.5: clampa qty do addon quando o estoque disponível cai
  useEffect(() => {
    const comps = item?.complements || [];
    if (!comps.length) return;
    const upd = {};
    comps.forEach((c, i) => {
      if (c.kind === "variant") return;
      const av = compAvail[i] || {};
      const rawMaxq = Math.max(1, parseInt(c.max_qty || 1, 10) || 1);
      const availNum = av.unlimited || av.available == null ? Infinity : Math.max(0, parseInt(av.available, 10) || 0);
      const effectiveMax = Math.min(rawMaxq, availNum === Infinity ? rawMaxq : availNum);
      const cur = parseInt(selAddons[i] || 0, 10) || 0;
      if (cur > effectiveMax) upd[i] = Math.max(0, effectiveMax);
    });
    if (Object.keys(upd).length) setSelAddons((m) => ({ ...m, ...upd }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compAvail, item?.id]);
  // V17.2: busca % de desconto do combo quando entramos via combo_from
  const [comboDiscountPct, setComboDiscountPct] = useState(0);
  useEffect(() => {
    if (!comboFrom || !item?.id) { setComboDiscountPct(0); return; }
    let ignore = false;
    (async () => {
      try {
        const r = await api.get(`/listings/${comboFrom}`);
        const src = r.data || {};
        const cb = (src.combos || []).find((x) => String(x.listing_id) === String(item.id));
        if (!cb) { if (!ignore) setComboDiscountPct(0); return; }
        // V17.9.2 — respeita combo_upsell_mode configurado no anúncio-pai
        const upsellMode = src.combo_upsell_mode || "per_item";
        let kit = null;
        try { kit = JSON.parse(sessionStorage.getItem("aj_kit_queue") || "null"); } catch { /* ignore */ }
        const kitSize = kit && kit.total ? parseInt(kit.total, 10) : 1;
        const nExtras = Math.max(0, kitSize - 1);
        const kitIds = (kit && Array.isArray(kit.listing_ids)) ? kit.listing_ids.map(String) : [];
        let pct = 0;
        if (upsellMode === "total_bundle") {
          pct = parseFloat(src.combo_bundle_discount_pct || 0) || 0;
        } else if (upsellMode === "progressive") {
          const tiers = [...(src.combo_progressive || [])].sort((a, b) => (parseInt(a.qty_extras, 10) || 0) - (parseInt(b.qty_extras, 10) || 0));
          for (const t of tiers) if ((parseInt(t.qty_extras, 10) || 0) <= nExtras) pct = parseFloat(t.discount_pct || 0) || 0;
        } else {
          const own = parseFloat(cb.discount_pct || 0) || 0;
          if (src.combo_stack_percentages) {
            let total = 0;
            for (const x of (src.combos || [])) {
              if (kitIds.includes(String(x.listing_id))) total += parseFloat(x.discount_pct || 0) || 0;
            }
            pct = total;
          } else {
            // compat legado (when_n_plus)
            const mode = cb.discount_mode || "on_added";
            let apply = own;
            if (mode === "when_n_plus") {
              const minItems = Math.max(2, parseInt(cb.min_items_for_discount || 2, 10));
              if (kitSize < minItems) apply = 0;
            }
            pct = apply;
          }
        }
        if (!ignore) setComboDiscountPct(Math.max(0, Math.min(90, pct)));
      } catch { if (!ignore) setComboDiscountPct(0); }
    })();
    return () => { ignore = true; };
  }, [comboFrom, item?.id]);

  // V18.0 — Preview do total do KIT (uma cobrança cobre principal + combos + adicionais).
  // Dispara quando há combos selecionados e datas/qtd definidas. Debounced 500ms.
  useEffect(() => {
    if (!item?.id || comboFrom) { setKitQuote(null); return; }
    const selectedIds = Object.entries(comboSelected).filter(([, on]) => on).map(([lid]) => lid);
    if (selectedIds.length === 0) { setKitQuote(null); return; }
    if (!start || !end) { setKitQuote(null); return; }
    // Inline (isAmbos/effectiveDelivery são calculados só quando o kit-quote roda)
    const _isAmbos = item?.regras_entrega === "ambos";
    const _effDelivery = _isAmbos ? deliveryChoice : item?.regras_entrega;
    const combo_items = selectedIds.map((lid) => ({ listing_id: lid, quantity: 1 }));
    const selected_complements = [
      ...Object.entries(selVariant).filter(([, oi]) => oi != null).map(([i, oi]) => ({ complement_index: parseInt(i, 10), option_index: oi, qty: 1 })),
      ...Object.entries(selAddons).filter(([, q]) => (parseInt(q, 10) || 0) > 0).map(([i, q]) => ({ complement_index: parseInt(i, 10), qty: parseInt(q, 10) })),
    ];
    let ignore = false;
    const t = setTimeout(async () => {
      setKitQuoteLoading(true);
      try {
        const { data } = await api.post("/bookings/kit-quote", {
          listing_id: item.id,
          start_date: start,
          end_date: end,
          start_time: startTime || undefined,
          end_time: endTime || undefined,
          quantity: Math.max(1, parseInt(quantity || 1, 10) || 1),
          delivery_choice: _isAmbos ? deliveryChoice : undefined,
          delivery_address_id: (_effDelivery === "eu_entrego" && selectedAddressId) ? selectedAddressId : undefined,
          payment_method: paymentMethod || "card",
          selected_complements,
          combo_items,
        });
        if (!ignore) setKitQuote(data);
      } catch (e) {
        if (!ignore) setKitQuote({ error: e?.response?.data?.detail || "Erro ao calcular kit" });
      } finally {
        if (!ignore) setKitQuoteLoading(false);
      }
    }, 500);
    return () => { ignore = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, comboSelected, start, end, startTime, endTime, quantity, selVariant, selAddons, paymentMethod, deliveryChoice, selectedAddressId, comboFrom]);

  if (notFound) return (
    <div className="max-w-md mx-auto p-12 text-center" data-testid="listing-not-found">
      <div className="font-heading font-black text-2xl">Anúncio não encontrado</div>
      <p className="text-slate-500 mt-2">Esse item pode ter sido removido ou está indisponível.</p>
      <button onClick={() => nav("/")} className="btn-primary mt-4">Voltar ao início</button>
    </div>
  );
  if (!item) return <div className="p-12 text-center text-slate-500">Carregando...</div>;

  // V17.9.6: em modo simulação, o dono deve ver EXATAMENTE como o cliente vê.
  // Portanto isOwner=false quando simulateMode=true (esconde banner "Este é o seu anúncio").
  const isOwner = !simulateMode && user && user.id === item.owner_id;
  // V15.1: compartilha o link de OG dinâmico (preview com a foto REAL do item) que
  // redireciona para o app/anúncio. Garante preview rico no WhatsApp/Instagram.
  const handleShare = async () => {
    const url = `${API}/share/listing/${item.id}`;
    const priceTxt = item.price_per_day ? ` por ${formatBRL(item.price_per_day)}/${unitLabelForItem(item)[0]}` : "";
    const text = `Confira "${item.title}"${priceTxt} no AlugaJá!`;
    const res = await shareContent({ title: item.title, text, url, dialogTitle: "Compartilhar anúncio" });
    if (res.method === "clipboard" && res.ok) toast.success("Link copiado!");
    else if (!res.ok && res.method === "none") toast.error("Não foi possível compartilhar.");
  };
  // V11.6: cartão salvo do usuário (final + bandeira; token fica no backend)
  const savedCard = user?.asaas_card_last4 ? { last4: user.asaas_card_last4, brand: user.asaas_card_brand } : null;
  const forgetCard = async () => {
    try {
      await api.delete("/me/card");
      await refresh();
      setUseOtherCard(false);
      toast.success("Cartão salvo removido.");
    } catch (e) {
      toast.error("Não foi possível remover o cartão agora.");
    }
  };
  const isAmbos = item.regras_entrega === "ambos";
  const days = start && end
    ? Math.max(0, Math.floor((new Date(end) - new Date(start)) / 86400000) + 1)
    : 0;
  const tipoCobranca = (item.tipo_cobranca || "DIARIO").toUpperCase();
  // V11.0: diária = blocos de 24h a partir do horário de retirada (quando há horários)
  const useBlocks24h = tipoCobranca === "DIARIO";
  let timeUnits;
  let durationHours = null;
  let fairCheckout = null;
  if (useBlocks24h) {
    if (start && end && startTime && endTime) {
      const s24 = new Date(`${start}T${startTime}:00`);
      const e24 = new Date(`${end}T${endTime}:00`);
      durationHours = Math.round(((e24 - s24) / 3600000) * 10) / 10;
      // V11.4 (item 4): tolerância de RETURN_TOLERANCE_MINUTES — o tempo que excede um bloco
      // de 24h por até a tolerância (ex.: 60min) NÃO cobra diária extra. Ex.: 25h = 1 diária.
      const tolH = (cfg.return_tolerance_minutes ?? 60) / 60;
      if (durationHours > 0) {
        const blocks = Math.floor(durationHours / 24);
        const remainder = durationHours - blocks * 24;
        timeUnits = Math.max(1, blocks + (remainder > tolH ? 1 : 0));
      } else {
        timeUnits = 0;
      }
      if (timeUnits > 0) {
        const paidHours = timeUnits * 24;
        const freeHours = Math.round((paidHours - durationHours) * 10) / 10;
        if (freeHours > 1) {
          const mr = new Date(s24.getTime() + paidHours * 3600000);
          const pad = (n) => String(n).padStart(2, "0");
          fairCheckout = {
            freeHours,
            maxDate: `${mr.getFullYear()}-${pad(mr.getMonth() + 1)}-${pad(mr.getDate())}`,
            maxTime: `${pad(mr.getHours())}:${pad(mr.getMinutes())}`,
          };
        }
      }
    } else {
      timeUnits = 0; // exige horários de retirada/devolução
    }
  } else {
    timeUnits = Math.max(1, Math.ceil(days / 30));
  }
  const periodReady = useBlocks24h
    ? (!!start && !!end && !!startTime && !!endTime && timeUnits > 0)
    : (timeUnits > 0);
  const unitLabel = tipoCobranca === "MENSAL" ? "mês(es)" : "diária(s)";
  const unitPriceLabel = `/ ${unitLabelForItem(item)[0]}`;
  const estoqueTotal = Math.max(1, parseInt(item.quantidade_total || 1, 10));

  // V17: regras de quantidade (mínimo + lote + calculadora de conversão)
  const qtyRules = item.qty_rules || {};
  const minQty = Math.max(1, parseInt(qtyRules.min_qty || 1, 10) || 1);
  const qtyStep = Math.max(1, parseInt(qtyRules.step || 1, 10) || 1);
  const snapQty = (n) => {
    const q = Math.max(minQty, parseInt(n, 10) || minQty);
    return minQty + Math.ceil((q - minQty) / qtyStep) * qtyStep;
  };
  const convActive = !!qtyRules.conversion_active;
  const convFactor = parseFloat(qtyRules.conversion_factor || 1) || 1;
  const convRequired = !!qtyRules.conversion_required;
  const hasQtyRules = minQty > 1 || qtyStep > 1 || convActive;
  // V17: complementos (variante/opcionais/consumíveis) + caução
  const comps = item.complements || [];
  const caucaoActive = !!item.caucao?.active;

  // Duração — stepper +/- por ciclos (1,2,3... meses/semanas) + ajuste fino de dias (só flexível).
  // Fechado usa o seletor de blocos próprio; diário usa data + horários.
  const showDurationStepper = isFlexible || (!isClosed && tipoCobranca === "MENSAL");
  const fineStep = cycleDays >= 30 ? 15 : cycleDays === 15 ? 5 : 1;
  const showFineDays = isFlexible && fineStep < cycleDays && cycleDays > 1;
  const curSpanDays = (start && end) ? days : 0;
  const curCycles = curSpanDays ? Math.max(1, Math.round(curSpanDays / cycleDays)) : 0;
  const setSpanDays = (totalDays) => {
    let base = start;
    if (!base) { base = onChangeStart(minStartDate); if (!base) return; }
    const span = Math.max(isFlexible ? minDaysFlex : cycleDays, totalDays);
    setEnd(adjustToOwnerDay(adjustToOperationalDay(addDaysISO(base, span - 1), "forward"), "forward"));
  };
  const changeCycles = (delta) => {
    if (!curSpanDays) { setSpanDays(Math.max(1, parseInt(item.min_cycles || 1, 10)) * cycleDays); return; }
    setSpanDays(Math.max(1, curCycles + delta) * cycleDays);
  };
  const changeDays = (delta) => {
    const base = curSpanDays || (isFlexible ? minDaysFlex : cycleDays);
    setSpanDays(base + delta);
  };
  // Epic 1/2/3: preço ciente de CICLO (flexível/fechado) = pro-rata (diária = preço_ciclo ÷ dias_ciclo).
  // Legado/diário mantém o motor de descontos por diárias. Isso corrige o "× 2 mês(es)".
  const cycleMode = isFlexible || isClosed;
  const billableDays = isClosed ? blocks * cycleDays : days;
  const nCyclesCalc = cycleDays ? billableDays / cycleDays : billableDays;
  // V17: variante soma no preço do ciclo (escala com duração/desconto/quantidade)
  const variantDelta = comps.reduce((s, c, i) => (
    c.kind === "variant" && selVariant[i] != null
      ? s + (parseFloat(c.options?.[selVariant[i]]?.price_delta || 0) || 0)
      : s
  ), 0);
  const effPrice = (parseFloat(item.price_per_day) || 0) + variantDelta;
  const pricing = cycleMode
    ? computeCyclePricing({
        pricePerCycle: effPrice, cycleDays, billableDays,
        nCycles: nCyclesCalc, quantity: Math.max(1, quantity), discountRules: item.discount_rules,
        quantityDiscountRules: item.quantity_discount_rules,
      })
    : computeListingDiscount({
        pricePerDay: effPrice, timeUnits, quantity: Math.max(1, quantity),
        startDate: start, endDate: end, tipoCobranca,
        discountRules: item.discount_rules, weekendPromo: item.weekend_promo, pickupTime: startTime,
        quantityDiscountRules: item.quantity_discount_rules,
      });
  // V17: opcionais/consumíveis — por ciclo (acompanha a duração) ou uma vez por locação
  const addonQty = (i) => Math.max(0, parseInt(selAddons[i] || 0, 10) || 0);

  // V17.10 — Aplica a seleção de um addon respeitando regras de GRUPO (exclusive / max N).
  // - exclusive: se newQty>0, zera todos os OUTROS addons do mesmo group_key.
  // - max:       ao atingir group_max seleções, o mais antigo do grupo é removido (FIFO).
  // NOTA: cálculo de FIFO é feito FORA do setSelAddons updater porque React StrictMode
  // invoca updaters duas vezes em dev — mutar groupOrderRef dentro da closure gera FIFO
  // duplicado/anulado. Aqui a ordem é computada sincronamente contra o valor ATUAL.
  const applyAddonPick = (idx, newQty) => {
    const q = Math.max(0, parseInt(newQty, 10) || 0);
    const c = comps[idx];
    // V18.8 — auto-abre a descrição do addon/consumable ao ADICIONAR pela 1ª vez
    if (q > 0 && (c?.kind === "addon" || c?.kind === "consumable")) {
      const hasDesc = !!(c?.description && String(c.description).trim());
      if (hasDesc) {
        setAddonDescOpen((m) => (m[idx] ? m : { ...m, [idx]: true }));
      }
    }
    const gkey = c?.group_key;
    if (!gkey || c?.kind !== "addon") {
      setSelAddons((m) => ({ ...m, [idx]: q }));
      return;
    }
    const mode = c.group_mode;
    if (mode === "exclusive" && q > 0) {
      // Zera os outros do grupo em uma única atualização
      const zeros = {};
      comps.forEach((cc, ii) => {
        if (ii !== idx && cc?.kind === "addon" && cc?.group_key === gkey) zeros[ii] = 0;
      });
      groupOrderRef.current[gkey] = [idx];
      setSelAddons((m) => ({ ...m, ...zeros, [idx]: q }));
      return;
    }
    if (mode === "max") {
      const max = Math.max(1, parseInt(c.group_max || 2, 10) || 2);
      // Ordem ATUAL sincronizada com o estado selAddons visível (sem depender só do ref).
      // Considera como "selecionado" qualquer addon do grupo com qty>0 no state atual.
      const currentOrder = (groupOrderRef.current[gkey] || [])
        .filter((x) => (selAddons[x] || 0) > 0 && x !== idx);
      // Garante que outros selecionados fora do ref também entrem na ordem (case: recarga)
      comps.forEach((cc, ii) => {
        if (ii !== idx && cc?.kind === "addon" && cc?.group_key === gkey && (selAddons[ii] || 0) > 0 && !currentOrder.includes(ii)) {
          currentOrder.push(ii);
        }
      });
      const nextPatch = {};
      if (q > 0) {
        currentOrder.push(idx);
        while (currentOrder.length > max) {
          const oldest = currentOrder.shift();
          nextPatch[oldest] = 0;
        }
      }
      groupOrderRef.current[gkey] = currentOrder;
      setSelAddons((m) => ({ ...m, ...nextPatch, [idx]: q }));
      return;
    }
    // Sem modo definido → comportamento normal
    setSelAddons((m) => ({ ...m, [idx]: q }));
  };
  const perCycleSum = comps.reduce((s, c, i) => (
    c.kind === "addon" && c.charge === "per_cycle" && addonQty(i) > 0
      ? s + (parseFloat(c.price || 0) || 0) * addonQty(i) : s
  ), 0);
  const onetimeTotal = comps.reduce((s, c, i) => (
    (c.kind === "consumable" || (c.kind === "addon" && c.charge !== "per_cycle")) && addonQty(i) > 0
      ? s + (parseFloat(c.price || 0) || 0) * addonQty(i) : s
  ), 0);
  const compCycleUnits = cycleMode ? nCyclesCalc : timeUnits;
  const compCycleTotal = Math.round(perCycleSum * (compCycleUnits > 0 ? compCycleUnits : 0) * 100) / 100;
  const complementsTotal = Math.round((compCycleTotal + onetimeTotal) * 100) / 100;
  // Rótulo do detalhamento: ciclo → "R$ X /mês · 1 mês e 10 dias"; senão "R$ X × N diária(s)"
  const [cycleUnitSing] = unitLabelForItem(item);
  const cycleSpanLabel = cycleMode && billableDays > 0 ? cycleBreakdownLabel(billableDays, cycleDays, item.cycle_unit) : null;
  const valorDiariasGross = pricing.gross;
  const valorDiarias = pricing.valor_diarias;
  const promos = describeListingPromos(item);
  const freteDesc = describeFrete(item);
  // Estimativa de frete p/ o endereço padrão do locatário (exibida antes do checkout)
  let freteEstimate = null;
  if (fretePreview) {
    if (fretePreview.has_address === false) {
      freteEstimate = { tone: "neutral", text: "Cadastre um endereço para ver o frete estimado até você." };
    } else if (fretePreview.a_combinar) {
      freteEstimate = null;
    } else if (fretePreview.needs_geo) {
      freteEstimate = { tone: "warn", text: "Adicione a localização (GPS) ao seu endereço para calcular o frete por km." };
    } else if (fretePreview.gratis_por_raio || (fretePreview.valor === 0)) {
      freteEstimate = { tone: "ok", text: `Frete grátis para o seu endereço${fretePreview.address_city ? ` (${fretePreview.address_city})` : ""}.` };
    } else if (fretePreview.valor > 0) {
      const isKm = item.frete_config?.tipo === "por_km";
      const dist = isKm && fretePreview.distance_km != null ? ` · ~${fretePreview.distance_km} km` : "";
      freteEstimate = { tone: "value", text: `Frete estimado até você: ${formatBRL(fretePreview.valor)}${dist}.` };
    }
  }
  // V11.0: opções de horário a partir do atendimento do dono (passos de 30min).
  // V18.7 — filtra horários já passados quando iso == HOJE no fuso do anúncio (+ 15min de folga).
  // Também respeita cutoff_retirada (max pickup mesmo-dia) e cutoff_entrega (max entrega mesmo-dia).
  const weekdayDom0 = (iso) => new Date(`${iso}T12:00:00`).getDay();
  const timeOptionsFor = (iso, kind = "pickup") => {
    if (!iso || !ownerSchedule?.dias) return [];
    const day = ownerSchedule.dias[String(weekdayDom0(iso))];
    if (!day?.aberto) return [];
    // Detecta se iso é HOJE no fuso do anúncio + calcula minutos atuais
    const tz = item?.timezone || "America/Sao_Paulo";
    let todayISO = ""; let nowMinutes = 0;
    try {
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(new Date());
      const y = parts.find((p) => p.type === "year")?.value;
      const mo = parts.find((p) => p.type === "month")?.value;
      const d = parts.find((p) => p.type === "day")?.value;
      const hh = parts.find((p) => p.type === "hour")?.value;
      const mm = parts.find((p) => p.type === "minute")?.value;
      todayISO = `${y}-${mo}-${d}`;
      let h = parseInt(hh, 10);
      if (h === 24) h = 0;
      nowMinutes = h * 60 + parseInt(mm, 10);
    } catch { /* browser antigo — sem filtro por hora atual */ }
    const isSameDay = iso === todayISO;
    const BUFFER_MIN = 15; // folga para chegar/preparar
    // Cutoff de retirada/entrega vindo do item OU config global
    const cutoffStr = kind === "delivery"
      ? (item?.cutoff_entrega || cfg?.horario_limite_entrega || "23:59")
      : (item?.cutoff_retirada || cfg?.horario_limite_retirada || "23:59");
    const [ch, cm] = cutoffStr.split(":").map(Number);
    // V18.7.1 — usar nullish para não tratar minuto 00 como falsy
    const cutoffMin = (Number.isFinite(ch) ? ch : 23) * 60 + (Number.isFinite(cm) ? cm : 59);

    const out = [];
    (day.intervalos || []).forEach((iv) => {
      const [ah, am] = iv.abre.split(":").map(Number);
      const [fh, fm] = iv.fecha.split(":").map(Number);
      for (let t = ah * 60 + am; t <= fh * 60 + fm; t += 30) {
        if (isSameDay && t < nowMinutes + BUFFER_MIN) continue;
        if (isSameDay && t > cutoffMin) continue;
        out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
      }
    });
    return out;
  };
  const valorFreteListing = parseFloat(item.valor_frete || 0);
  const willCharge_frete = deliveryChoice === "eu_entrego";
  // Epic 2: frete dinâmico (espelha core.compute_frete; backend é a autoridade no checkout)
  const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };
  const selectedAddress = addresses.find((a) => a.id === selectedAddressId) || null;
  const computeFrete = () => {
    const fc = item.frete_config;
    if (!fc) return { valor: valorFreteListing, a_combinar: false, needs_geo: false, distance_km: null };
    const tipo = fc.tipo || "fixo";
    if (tipo === "gratis") return { valor: 0, a_combinar: false, needs_geo: false, gratis: true };
    if (tipo === "combinar") return { valor: 0, a_combinar: true, needs_geo: false };
    const ilat = item.lat, ilng = item.lng, dlat = selectedAddress?.lat, dlng = selectedAddress?.lng;
    let dist = null;
    if (ilat != null && ilng != null && dlat != null && dlng != null) dist = Math.round(haversineKm(ilat, ilng, dlat, dlng) * 10) / 10;
    const raio = parseFloat(fc.raio_gratis_km || 0);
    if (dist != null && raio > 0 && dist <= raio) return { valor: 0, a_combinar: false, needs_geo: false, distance_km: dist, gratis_por_raio: true };
    if (tipo === "fixo") return { valor: parseFloat(fc.valor_fixo || 0), a_combinar: false, needs_geo: false, distance_km: dist };
    if (tipo === "por_km") {
      if (dist == null) return { valor: 0, a_combinar: false, needs_geo: true, distance_km: null };
      const mult = fc.ida_volta ? 2 : 1;
      return { valor: Math.round(parseFloat(fc.valor_por_km || 0) * dist * mult * 100) / 100, a_combinar: false, needs_geo: false, distance_km: dist, mult };
    }
    return { valor: 0, a_combinar: false, needs_geo: false };
  };
  const freteInfo = willCharge_frete ? computeFrete() : { valor: 0, a_combinar: false, needs_geo: false };
  const valorFrete = willCharge_frete ? (freteInfo.valor || 0) : 0;
  // 10% comissão + R$ 0,50 do locador + taxa do gateway → tudo descontado do locador no payout.
  const taxaPercentual = 0;  // V8.5: locatário não paga mais 10%
  // V9.8: ao pagar 100% com saldo não há taxa; cartão/PIX (inclui split) têm R$ 0,50.
  const base = valorDiarias + complementsTotal + valorFrete;
  const comboDiscountValue = comboDiscountPct > 0 ? Math.round((valorDiarias + complementsTotal) * comboDiscountPct) / 100 : 0;
  const baseAfterCombo = Math.max(0, base - comboDiscountValue);
  // V18.0 — Kit unificado: soma dos combos selecionados (valor já com desconto vindo do backend)
  const kitCombosTotal = (kitQuote && !kitQuote.error) ? (parseFloat(kitQuote.combos_total) || 0) : 0;
  const baseWithKit = baseAfterCombo + kitCombosTotal;
  const coversWithWallet = paymentMethod === "wallet" && walletBalance != null && walletBalance >= baseWithKit;
  const taxaFixa = coversWithWallet ? 0 : 0.50;
  const taxaServico = taxaPercentual + taxaFixa;
  const total = baseWithKit + taxaServico;
  // V17: valor da caução (fixo ou % sobre o valor da locação, teto 10× o ciclo)
  const caucaoAmount = caucaoActive ? (() => {
    const cap = (parseFloat(item.price_per_day) || 0) * 10;
    const cc = item.caucao || {};
    let v = (cc.type === "percent")
      ? (valorDiarias + complementsTotal) * ((parseFloat(cc.value || 0) || 0) / 100)
      : (parseFloat(cc.value || 0) || 0);
    if (cap > 0) v = Math.min(v, cap);
    return Math.round(v * 100) / 100;
  })() : 0;
  // Split fica disponível quando escolheu Saldo, o saldo NÃO cobre o total, mas há algum saldo (> 0)
  const walletShort = paymentMethod === "wallet" && walletBalance != null && walletBalance < total;
  const splitEligible = walletShort && (walletBalance || 0) > 0;
  const splitActive = splitEligible && !!splitGateway;
  // Locatário sem saldo nenhum não tem split — precisa trocar de método
  const walletInsufficient = walletShort && !splitActive;
  // Cálculo (estimado) das partes do split — backend é a fonte da verdade
  const SPLIT_GATEWAY_MIN = 5.0;
  let walletPart = 0, gatewayPart = 0;
  if (splitActive) {
    const avail = walletBalance || 0;
    const remainder = Math.round((total - avail) * 100) / 100;
    if (remainder >= SPLIT_GATEWAY_MIN) { walletPart = avail; gatewayPart = remainder; }
    else { gatewayPart = SPLIT_GATEWAY_MIN; walletPart = Math.round((total - SPLIT_GATEWAY_MIN) * 100) / 100; }
  }
  // Método efetivo enviado ao backend
  const effectivePM = splitActive ? (splitGateway === "card" ? "wallet_card" : "wallet_pix") : paymentMethod;
  const isCardFlow = effectivePM === "card" || effectivePM === "wallet_card";
  const isPixFlow = effectivePM === "pix" || effectivePM === "wallet_pix";

  const book = () => {
    if (!start || !end || !periodReady) { toast.error(useBlocks24h ? "Selecione as datas e horários" : "Selecione as datas"); return; }
    // V7.3: bloqueia datas passadas em JS também (alguns mobile browsers não respeitam min)
    const todayISO = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];
    if (start < todayISO) { toast.error("Data de retirada não pode estar no passado"); return; }
    if (end < start) { toast.error("Data de devolução deve ser igual ou depois da retirada"); return; }
    if (isAmbos && !deliveryChoice) { toast.error("Escolha entrega ou retirada"); return; }
    // Epic 2: frete por km exige localização (GPS) no endereço de entrega
    if (effectiveDelivery === "eu_entrego" && freteInfo.needs_geo) {
      toast.error("Adicione a localização (GPS) ao endereço de entrega para calcular o frete por km.");
      return;
    }
    if (stockInfo && !stockInfo.ok) {
      toast.error(`Estoque insuficiente: disponível ${stockInfo.disponivel} de ${stockInfo.estoque_total}`);
      return;
    }
    if (cutoffInfo && cutoffInfo.same_day && !cutoffInfo.ok) {
      toast.error(cutoffInfo.blocked_reason || "Horário limite para hoje já expirou");
      return;
    }
    if (walletInsufficient) {
      toast.error(`Saldo insuficiente. Você tem ${formatBRL(walletBalance)} e precisa de ${formatBRL(total)}. Escolha completar com Cartão ou PIX.`);
      return;
    }
    // V17: variante obrigatória + regras de quantidade
    for (let i = 0; i < comps.length; i++) {
      const c = comps[i];
      if (c.kind === "variant" && c.required && selVariant[i] == null) {
        toast.error(`Escolha uma opção em "${c.name}" para continuar.`);
        return;
      }
    }
    const qNum = Math.max(1, parseInt(quantity || 1, 10) || 1);
    if (qNum < minQty) { toast.error(`Quantidade mínima para este item: ${minQty}.`); return; }
    if ((qNum - minQty) % qtyStep !== 0) { toast.error(`A partir de ${minQty}, a quantidade aumenta de ${qtyStep} em ${qtyStep}.`); return; }
    // V7.4: valida dias de operação do locador antes de chamar o backend
    if (Array.isArray(item.dias_operacao) && item.dias_operacao.length > 0 && item.dias_operacao.length < 7) {
      const dias = new Set(item.dias_operacao);
      const labels = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
      // Trata datas como local-noon para evitar timezone shift
      const sDay = new Date(start + "T12:00:00").getDay();
      const eDay = new Date(end + "T12:00:00").getDay();
      if (!dias.has(sDay)) { toast.error(`O locador não opera ${labels[sDay]} (data inicial).`); return; }
      if (!dias.has(eDay)) { toast.error(`O locador não opera ${labels[eDay]} (data final).`); return; }
    }
    requireAuth(async () => {
      // V18.0 — Kit unificado: enviamos combo_items no MESMO checkout (uma cobrança cobre tudo).
      // Removido: aj_kit_queue sessionStorage flow (legado — múltiplos checkouts em cascata).
      // V9.0/9.8: CARTÃO (e split Saldo+Cartão) usam formulário Asaas in-page (pré-autorização/escrow)
      if (isCardFlow) {
        // V11.6: cartão salvo → paga com 1 toque (sem reabrir o formulário)
        if (savedCard && !useOtherCard && !splitActive) {
          await doCheckout({ use_saved_card: true });
          return;
        }
        setCardError("");
        setCardSuccess(false);
        setShowCardForm(true);
        return;
      }
      await doCheckout({});
    });
  };

  // V8.1/9.8: chamada efetiva ao /bookings/checkout (PIX, wallet, split ou cartão com token)
  const doCheckout = async (extras) => {
    setLoading(true);
    setCardError("");
    setCardSuccess(false);
    try {
      // V18.0 — combos selecionados vão como combo_items para checkout unificado
      const kitComboIds = Object.entries(comboSelected).filter(([, on]) => on).map(([lid]) => lid);
      const kitComboItems = (!comboFrom && kitComboIds.length > 0)
        ? kitComboIds.map((lid) => ({ listing_id: lid, quantity: 1 }))
        : undefined;
      const { data } = await api.post("/bookings/checkout", {
        listing_id: id,
        start_date: start,
        end_date: end,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        origin_url: window.location.origin,
        client_ip: await getClientIp(),
        delivery_choice: isAmbos ? deliveryChoice : undefined,
        delivery_address_id: (effectiveDelivery === "eu_entrego" && selectedAddressId) ? selectedAddressId : undefined,
        payment_method: effectivePM,
        accept_terms: true,
        quantity: Math.max(1, parseInt(quantity || 1, 10) || 1),
        delivery_notes: deliveryNotes.trim() || undefined,
        // V17: complementos escolhidos
        selected_complements: [
          ...Object.entries(selVariant).filter(([, oi]) => oi != null).map(([i, oi]) => ({ complement_index: parseInt(i, 10), option_index: oi, qty: 1 })),
          ...Object.entries(selAddons).filter(([, q]) => (parseInt(q, 10) || 0) > 0).map(([i, q]) => ({ complement_index: parseInt(i, 10), qty: parseInt(q, 10) })),
        ],
        // V17.2: envia a medida da calculadora quando ativa (usada em regras de obrigatoriedade proporcional)
        measure_input: convActive && convInput ? (parseFloat(convInput) || null) : null,
        // V17.2 (legado) — quando o cliente entra numa página de combo via legacy link
        combo_source_listing_id: comboFrom || undefined,
        // V18.0 — kit unificado: itens extras do combo (mesmo dono, mesmas datas) em UMA cobrança
        combo_items: kitComboItems,
        ...extras,
      });
      // Wallet integral → redirect local
      if (effectivePM === "wallet" && data.url) {
        window.location.href = data.url;
        return "ok";
      }
      // V13.2: reserva longa (caiu no MP, retirada distante) → confirmada, pagamento perto da data
      if (data.scheduled && data.url) {
        const d = data.scheduled_charge_at ? new Date(data.scheduled_charge_at).toLocaleDateString("pt-BR") : null;
        toast.success(d
          ? `Reserva confirmada! No dia ${d} avisaremos para você concluir o pagamento.`
          : "Reserva confirmada! Avisaremos perto da retirada para concluir o pagamento.");
        setTimeout(() => { window.location.href = data.url; }, 1400);
        return "ok";
      }
      // V9.1/9.8: PIX (e split Saldo+PIX) → mostra QR in-app
      if (isPixFlow && data.payment?.qr_code) {
        setPixCheckout(data.payment);
        setCurrentBookingId(data.booking_id);
        setLoading(false);
        return "pix";
      }
      // V9.1/9.8: Cartão (e split Saldo+Cartão, pré-autorização Asaas) → authorized/paid = sucesso
      if (isCardFlow && data.payment) {
        const st = data.payment.internal_status;
        if (st === "authorized" || st === "paid") {
          setCardSuccess(true);
          toast.success(splitActive
            ? "Pagamento autorizado! Saldo reservado + cartão pré-autorizado até a retirada."
            : "Pagamento autorizado! O valor fica reservado até a retirada.");
          setTimeout(() => {
            nav(`/reservas/sucesso?session_id=${data.session_id}&booking_id=${data.booking_id}`);
          }, 1100);
          return "approved";
        } else if (st === "refunded" || st === "cancelled") {
          setCardError("Pagamento recusado. Verifique os dados do cartão e tente novamente.");
          setLoading(false);
          return "rejected";
        } else {
          toast.info("Pagamento em análise — vamos confirmar e te avisar por e-mail.");
          nav(`/minhas-reservas`);
          return "pending";
        }
      }
      // Fallback: redirect se url estiver presente
      if (data.url) {
        window.location.href = data.url;
      } else {
        setLoading(false);
      }
      return "ok";
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = formatApiError(detail);
      // V12.12/V13.3: cartão salvo indisponível OU recusado sem token MP → pedir o cartão digitado
      const savedCardGone = status === 409 || (typeof detail === "string" && detail.includes("não está mais disponível"));
      if (savedCardGone) {
        await refresh();
        setUseOtherCard(true);
        setShowCardForm(true);
        setCardError(
          (typeof detail === "string" && detail) ||
          "Seu cartão salvo precisou ser atualizado. Por favor, adicione o cartão novamente para concluir o pagamento."
        );
        setCardSuccess(false);
        setLoading(false);
        return "saved_card_gone";
      }
      // No fluxo de cartão, mostra o erro dentro do modal (sem fechar) para nova tentativa
      if (paymentMethod === "card" && showCardForm) {
        setCardError(msg);
      } else {
        toast.error(msg);
        setShowCardForm(false);
      }
      setLoading(false);
      return "error";
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { requireAuth(); return; }
    try {
      await api.post("/reviews", { listing_id: id, rating, comment });
      const r = await api.get(`/reviews/${id}`);
      setReviews(r.data);
      const it = await api.get(`/listings/${id}`);
      setItem(it.data);
      setComment("");
      toast.success("Avaliação enviada!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const entregaLabel = {
    "eu_entrego": "Dono entrega",
    "locatario_busca": "Você busca",
    "ambos": "Entrega ou retirada",
  }[item.regras_entrega] || "A combinar";
  const EntregaIcon = item.regras_entrega === "eu_entrego" ? Truck : item.regras_entrega === "locatario_busca" ? House : Package;

  return (
    <div className="max-w-7xl mx-auto" data-testid="listing-detail">
      {simulateMode && (
        <div className="rounded-xl border-2 border-amber-400 bg-amber-100 text-amber-900 px-3 py-2 mb-2 flex items-center justify-between gap-2 text-sm font-bold" data-testid="simulate-banner">
          <span>🧪 <b>Modo simulação</b> — nenhuma reserva será criada. Ajuste datas/combos/opcionais e veja como o cliente vê.</span>
          <button onClick={() => nav(`/itens/${item.id}`)} className="text-[11px] font-black text-amber-900 border-2 border-amber-500 rounded-full px-2.5 py-1 hover:bg-amber-200 shrink-0" data-testid="simulate-exit">
            Sair
          </button>
        </div>
      )}
      {isResume && (
        <div className="bg-amber-50 border-b-2 border-amber-300 px-4 py-2.5 flex items-start gap-2" data-testid="resume-checkout-banner">
          <Warning size={18} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-xs sm:text-sm text-amber-900">
            <b>Retomando seu checkout:</b> seus dados foram preenchidos. Confira e finalize o pagamento para garantir o item.
          </div>
        </div>
      )}
      <div className="aspect-[4/3] md:aspect-[16/8] bg-slate-100 relative">
        <button
          onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
          className="absolute left-3 z-20 w-10 h-10 rounded-full bg-black/45 hover:bg-black/60 text-white grid place-items-center backdrop-blur transition-colors"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
          aria-label="Voltar"
          data-testid="listing-detail-back"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
        {item.images?.[imgIndex] ? (
          <img
            src={fileUrl(item.images[imgIndex])}
            alt={item.title}
            onClick={() => setLightboxOpen(true)}
            className="w-full h-full object-cover cursor-zoom-in"
            data-testid="listing-main-image"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400">Sem foto</div>
        )}
      </div>
      {item.images?.length > 1 && (
        <div className="flex gap-2 p-3 overflow-x-auto">
          {item.images.map((p, i) => (
            <button key={`${p}-${i}`} onClick={() => setImgIndex(i)}
              className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 ring-2 ${i === imgIndex ? "ring-[#FF6B35]" : "ring-transparent"}`}
              data-testid={`thumb-${i}`}>
              <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightboxOpen && item.images?.length > 0 && (
        <ImageLightbox
          images={item.images}
          index={imgIndex}
          onIndexChange={setImgIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
      {compLightboxImg && (
        <ImageLightbox
          images={[compLightboxImg]}
          index={0}
          onIndexChange={() => {}}
          onClose={() => setCompLightboxImg(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-4 py-4">
        <div className="lg:col-span-2 space-y-5 min-w-0">
          {comboFrom && (
            <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-3 flex items-center gap-3" data-testid="kit-continue-banner">
              <div className="rounded-full bg-emerald-600 text-white w-9 h-9 flex items-center justify-center shrink-0 font-black">
                {kitPos || 2}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-black text-emerald-900">
                  Continue seu kit ({kitPos || 2}{kitTotal ? ` de ${kitTotal}` : ""})
                </div>
                <div className="text-[11px] text-emerald-800">
                  Mesmo período do item anterior. O desconto do combo é aplicado automaticamente no total.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  try { sessionStorage.removeItem("aj_kit_queue"); } catch { /* ignore */ }
                  nav("/minhas-reservas");
                }}
                className="text-[11px] font-bold text-emerald-700 shrink-0 underline"
                data-testid="kit-abandon-btn"
              >
                Sair do kit
              </button>
            </div>
          )}
          <div>
            <span className="aj-badge">{item.category}</span>
            <div className="flex items-start justify-between gap-3 mt-2">
              <h1 className="font-heading text-2xl md:text-4xl font-black" data-testid="listing-title">{item.title}</h1>
              <button onClick={handleShare} data-testid="listing-share-btn"
                className="flex-shrink-0 mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-[#004E98] border-2 border-[#004E98]/20 hover:bg-[#004E98]/5 rounded-full px-3 py-1.5 transition-colors">
                <ShareNetwork size={16} weight="bold" /> <span className="hidden sm:inline">Compartilhar</span>
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
              <span className="flex items-center gap-1"><MapPin size={14} weight="bold" /> {item.city} - {item.uf || "MT"}</span>
              {item.rating_count > 0 && (
                <span className="flex items-center gap-1" title="Nota do item" data-testid="listing-item-rating">
                  <Star size={14} weight="fill" color="#FFCC00" /> {item.rating_avg.toFixed(1).replace(".", ",")} ({item.rating_count})
                  <span className="text-[10px] text-slate-400 font-medium">item</span>
                </span>
              )}
              {item.owner_id ? (
                <Link to={`/u/${item.owner_id}`} className="flex items-center gap-1 text-[#004E98] font-semibold hover:underline" data-testid="listing-owner-link">
                  <User size={14} weight="bold" /> {item.owner_name}
                  {item.owner_rating_count > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-amber-600" title="Nota do dono" data-testid="listing-owner-rating">
                      <Star size={12} weight="fill" color="#FFCC00" /> {item.owner_rating_avg.toFixed(1).replace(".", ",")}
                    </span>
                  )}
                </Link>
              ) : (
                <span className="flex items-center gap-1"><User size={14} weight="bold" /> {item.owner_name}</span>
              )}
              <span className="flex items-center gap-1"><EntregaIcon size={14} weight="bold" /> {entregaLabel}</span>
              {freteDesc.badge && (
                <span className={`font-semibold ${freteDesc.badge === "Frete grátis" ? "text-emerald-600" : freteDesc.badge === "Frete a combinar" ? "text-slate-500" : "text-[#FF6B35]"}`} data-testid="listing-frete-badge">
                  {freteDesc.badge}
                </span>
              )}
              {(item.cutoff_entrega || item.cutoff_retirada) && (
                <span className="flex items-center gap-1 text-[11px] text-slate-500" data-testid="listing-cutoff-info">
                  <Clock size={12} weight="bold" />
                  Limite p/ hoje:
                  {item.cutoff_entrega && <b className="ml-1">entrega {item.cutoff_entrega}</b>}
                  {item.cutoff_entrega && item.cutoff_retirada && " · "}
                  {item.cutoff_retirada && <b>retirada {item.cutoff_retirada}</b>}
                </span>
              )}
              {Array.isArray(item.dias_operacao) && item.dias_operacao.length > 0 && item.dias_operacao.length < 7 && (
                <span className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200" data-testid="listing-dias-info">
                  <Calendar size={12} weight="bold" />
                  Opera: {item.dias_operacao.map((d) => ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][d]).join(", ")}
                </span>
              )}
            </div>
          </div>

          <div className="aj-card p-5">
            <h3 className="font-heading font-bold text-lg mb-2">Descrição</h3>
            <p className="text-slate-700 whitespace-pre-line">{item.description}</p>
            {item.tags && item.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="listing-tags">
                {item.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-bold">#{t}</span>
                ))}
              </div>
            )}
          </div>

          {promos.hasPromo && (
            <div className="aj-card p-5 border-2 border-emerald-200 bg-emerald-50/40" data-testid="listing-promos">
              <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2 text-emerald-800">
                <Confetti size={20} weight="fill" /> Promoções e descontos
              </h3>
              <ul className="space-y-2">
                {promos.lines.map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`promo-line-${i}`}>
                    {l.type === "weekend"
                      ? <Confetti size={16} weight="fill" className="text-[#FF6B35] shrink-0 mt-0.5" />
                      : <Lightning size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />}
                    <span>{l.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(item.combos_expanded || []).length > 0 && (() => {
            // V17.9.2 — descrição dinâmica conforme combo_upsell_mode
            const upsellMode = item.combo_upsell_mode || "per_item";
            const stack = !!item.combo_stack_percentages;
            const bundlePct = parseFloat(item.combo_bundle_discount_pct || 0) || 0;
            const tiers = [...(item.combo_progressive || [])].sort((a, b) => (parseInt(a.qty_extras, 10) || 0) - (parseInt(b.qty_extras, 10) || 0));
            let helper = "Marque o que quer levar no mesmo período — o desconto é aplicado automaticamente.";
            if (upsellMode === "total_bundle" && bundlePct > 0) {
              helper = `Marque qualquer combo abaixo para ganhar ${bundlePct}% off em cada extra do kit.`;
            } else if (upsellMode === "progressive" && tiers.length > 0) {
              helper = `Quanto mais extras, maior o desconto: ${tiers.map((t) => `${t.qty_extras}+ → ${t.discount_pct}%`).join(" · ")}.`;
            } else if (upsellMode === "per_item" && stack) {
              helper = "Marque vários combos — os percentuais SOMAM e aplicam a cada extra do kit.";
            }
            const selectedCount = Object.values(comboSelected).filter(Boolean).length;
            const summedPct = stack && upsellMode === "per_item"
              ? (item.combos_expanded || []).filter((cb) => comboSelected[cb.listing_id])
                  .reduce((acc, cb) => acc + (parseFloat(cb.discount_pct || 0) || 0), 0)
              : 0;
            const currentTierPct = upsellMode === "progressive" && selectedCount > 0
              ? (() => { let p = 0; for (const t of tiers) if ((parseInt(t.qty_extras, 10) || 0) <= selectedCount) p = parseFloat(t.discount_pct || 0) || 0; return p; })()
              : 0;
          return (
            <div className="aj-card p-5 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white" data-testid="listing-combos">
              <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2 text-emerald-800">
                <Confetti size={20} weight="fill" /> Kit — alugue tudo junto
              </h3>
              <p className="text-[11px] text-slate-500 mb-3">{helper}</p>
              {(stack && summedPct > 0) || (currentTierPct > 0) ? (
                <div className="text-[11px] font-black text-emerald-800 bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5 mb-2" data-testid="combo-live-discount">
                  Desconto ativo agora: {(stack ? summedPct : currentTierPct).toFixed(0)}% em cada extra
                </div>
              ) : null}
              <div className="space-y-2">
                {item.combos_expanded.map((cb, i) => {
                  const on = !!comboSelected[cb.listing_id];
                  const unavailable = cb.is_available === false;
                  return (
                    <label key={i} className={`flex items-center gap-3 rounded-xl border-2 ${unavailable ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed" : (on ? "border-emerald-500 bg-emerald-50 cursor-pointer" : "border-emerald-200 bg-white cursor-pointer")} p-2.5 transition`} data-testid={`combo-offer-${i}`}>
                      <input type="checkbox" checked={on} disabled={unavailable}
                        onChange={(e) => setComboSelected((s) => ({ ...s, [cb.listing_id]: e.target.checked }))}
                        className="w-5 h-5 accent-emerald-600 shrink-0 disabled:opacity-40"
                        data-testid={`combo-offer-${i}-check`} />
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                        {cb.image ? <img src={fileUrl(cb.image)} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-black text-slate-800 truncate">{cb.title}</div>
                        <div className="text-[11px] text-slate-500">
                          {formatBRL(cb.price_per_day)}/{unitLabelForItem(cb)[0]}
                          {cb.note ? <span className="text-emerald-700 font-bold"> · {cb.note}</span> : null}
                        </div>
                        {unavailable && (
                          <div className="text-[10px] font-bold text-red-600 mt-0.5" data-testid={`combo-offer-${i}-unavailable`}>
                            {cb.unavailable_reason === "paused" ? "⛔ Pausado no momento" : "⛔ Sem estoque agora"}
                          </div>
                        )}
                        {cb.discount_mode === "when_n_plus" && cb.min_items_for_discount && (
                          <div className="text-[10px] text-emerald-700 mt-0.5">
                            Desconto vale com <b>{cb.min_items_for_discount}+</b> itens no kit
                          </div>
                        )}
                      </div>
                      {upsellMode === "per_item" && !stack && parseFloat(cb.discount_pct || 0) > 0 && !unavailable && (
                        <span className="shrink-0 text-[10px] font-black text-white bg-emerald-600 rounded-full px-2 py-1">
                          {parseFloat(cb.discount_pct)}% OFF
                        </span>
                      )}
                      <Link to={`/itens/${cb.listing_id}`} onClick={(e) => e.stopPropagation()}
                        className="text-[10px] text-emerald-700 underline shrink-0" data-testid={`combo-offer-${i}-details`}>
                        detalhes
                      </Link>
                    </label>
                  );
                })}
              </div>
              {Object.values(comboSelected).some(Boolean) && (
                <div className="text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 mt-3 space-y-1.5" data-testid="combo-kit-hint">
                  <div className="font-bold flex items-center gap-1.5">
                    <CheckCircle size={14} weight="fill" className="text-emerald-600" /> Kit adicionado — pagamento único
                  </div>
                  {kitQuoteLoading && (
                    <div className="text-[10px] text-slate-500" data-testid="kit-total-loading">Calculando total do kit...</div>
                  )}
                  {kitQuote && !kitQuote.error && !kitQuoteLoading && (
                    <>
                      {(kitQuote.combos || []).map((c) => (
                        <div key={c.listing_id} className="flex justify-between text-[11px]" data-testid={`kit-combo-line-${c.listing_id}`}>
                          <span className="truncate mr-2">
                            {c.title}
                            {c.combo_discount_pct ? <span className="text-emerald-700 font-bold"> · -{parseFloat(c.combo_discount_pct).toFixed(0)}%</span> : null}
                          </span>
                          <b>+ {formatBRL(c.valor_diarias)}</b>
                        </div>
                      ))}
                      <div className="flex justify-between text-[11px] pt-1.5 border-t border-emerald-200">
                        <span>Extras do kit:</span>
                        <b data-testid="kit-extras-total">{formatBRL(kitQuote.combos_total)}</b>
                      </div>
                    </>
                  )}
                  {kitQuote && kitQuote.error && (
                    <div className="text-[10px] text-red-600 font-bold" data-testid="kit-quote-error">
                      {kitQuote.error}
                    </div>
                  )}
                  <div className="text-[10px] text-emerald-700">
                    Uma única cobrança cobre o item principal, todos os combos, adicionais e opcionais.
                  </div>
                </div>
              )}
            </div>
          ); })()}

          {(effectiveDelivery === "eu_entrego" || isAmbos) && freteDesc.lines.length > 0 && (
            <div className="aj-card p-5 border-2 border-[#FF6B35]/20 bg-[#FF6B35]/[0.04]" data-testid="listing-frete-policy">
              <h3 className="font-heading font-bold text-lg mb-2 flex items-center gap-2 text-[#FF6B35]">
                <Truck size={20} weight="fill" /> Entrega e frete
              </h3>
              <ul className="space-y-1.5">
                {freteDesc.lines.map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`frete-line-${i}`}>
                    <span className="text-[#FF6B35] mt-0.5">•</span>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
              {freteEstimate && (
                <div
                  className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold flex items-center gap-2 ${
                    freteEstimate.tone === "ok" ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                    : freteEstimate.tone === "value" ? "bg-[#004E98]/5 border border-[#004E98]/20 text-[#004E98]"
                    : freteEstimate.tone === "warn" ? "bg-amber-50 border border-amber-200 text-amber-800"
                    : "bg-slate-50 border border-slate-200 text-slate-600"}`}
                  data-testid="frete-estimate"
                >
                  {freteEstimate.tone === "ok" ? <CheckCircle size={16} weight="fill" className="shrink-0" />
                    : <Truck size={16} weight="fill" className="shrink-0" />}
                  <span>{freteEstimate.text}</span>
                </div>
              )}
              <p className="text-[11px] text-slate-500 pt-2">O valor exato do frete é confirmado no checkout, conforme o endereço de entrega escolhido.</p>
            </div>
          )}

          <div className="aj-card p-5">
            <h3 className="font-heading font-bold text-lg mb-3">Avaliações</h3>
            {reviews.length === 0 ? (
              <p className="text-slate-500 text-sm">Ainda sem avaliações.</p>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="border-b last:border-b-0 border-slate-100 pb-3 last:pb-0">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-sm">{r.user_name}</div>
                      <div className="flex items-center gap-0.5 text-sm">
                        <Star size={13} weight="fill" color="#FFCC00" /> {r.rating}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}
            {user && canReview && (
              <form onSubmit={submitReview} className="mt-4 pt-4 border-t border-slate-100" data-testid="review-form">
                <div className="font-bold text-sm mb-2">Deixe sua avaliação</div>
                <div className="flex gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`}>
                      <Star size={26} weight={n <= rating ? "fill" : "regular"} color="#FFCC00" />
                    </button>
                  ))}
                </div>
                <textarea required value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="Conte sua experiência..." rows={3} className="aj-input" data-testid="review-comment" />
                <button type="submit" className="btn-blue mt-2" data-testid="submit-review-btn">Enviar</button>
              </form>
            )}
            {user && !canReview && !isOwner && (
              <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500" data-testid="review-locked">
                💬 Apenas usuários que concluíram um aluguel desta ferramenta podem avaliar.
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-1 min-w-0">
          <div className="lg:sticky lg:top-24 aj-card p-5 space-y-4">
            {(() => {
              const range = getListingPriceRange(item);
              const anyVariantChosen = comps.some((c, i) => c.kind === "variant" && selVariant[i] != null);
              const showRange = range.hasRange && !anyVariantChosen;
              return (
                <div className="flex items-baseline gap-1 flex-wrap">
                  {showRange ? (
                    <>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mr-1" data-testid="detail-price-from-label">
                        A partir de
                      </span>
                      <div className="font-heading text-3xl font-black text-[#004E98]" data-testid="detail-price-min">
                        {formatBRL(range.min)}
                      </div>
                      {range.max > range.min && (
                        <div className="text-sm font-semibold text-slate-500" data-testid="detail-price-range">
                          – {formatBRL(range.max)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="font-heading text-3xl font-black text-[#004E98]" data-testid="detail-price-effective">
                      {formatBRL(effPrice)}
                    </div>
                  )}
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{unitPriceLabel}</div>
                  {estoqueTotal > 1 && (
                    <div className="ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                      {estoqueTotal} em estoque
                    </div>
                  )}
                </div>
              );
            })()}

            {isOwner ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center space-y-3" data-testid="owner-view-notice">
                <p className="text-sm text-blue-900 font-semibold">Este é o seu anúncio</p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <button onClick={() => nav(`/editar-anuncio/${id}`)} className="btn-blue inline-flex text-sm py-2 px-4" data-testid="edit-listing-btn">
                    <PencilSimple size={16} weight="bold" /> Editar
                  </button>
                  <button onClick={() => nav(`/itens/${id}?simulate=1`)} className="inline-flex items-center gap-2 rounded-full border-2 border-amber-300 bg-amber-50 text-amber-800 font-bold text-sm py-2 px-4 hover:bg-amber-100 transition" data-testid="simulate-owner-btn">
                    🧪 Ver como cliente
                  </button>
                </div>
                <p className="text-[11px] text-blue-800/80">
                  Toque em <b>Ver como cliente</b> para simular a experiência de aluguel (variantes, opcionais, combos e preço final).
                </p>
              </div>
            ) : (
              <>
                {/* V7.8: Aviso de dias não-operacionais ANTES do calendário, pra o usuário não tomar susto */}
                {Array.isArray(item.dias_operacao) && item.dias_operacao.length > 0 && item.dias_operacao.length < 7 && (() => {
                  const labels = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
                  const operates = new Set(item.dias_operacao);
                  const blocked = [0,1,2,3,4,5,6].filter((d) => !operates.has(d)).map((d) => labels[d]);
                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800" data-testid="non-operational-days-notice">
                      <Warning size={11} weight="fill" className="inline mr-1" />
                      O locador <b>não opera {blocked.join(", ")}</b>. Datas escolhidas nesses dias serão ajustadas automaticamente.
                    </div>
                  );
                })()}

                {todayBlocked && !(start && start > todayISO) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800" data-testid="today-blocked-notice">
                    <Warning size={12} weight="fill" className="inline mr-1" />
                    <b>Reservas para hoje encerradas.</b> O horário limite de {effectiveDelivery === "eu_entrego" ? "entrega" : "retirada"} é até as <b>{effectiveDelivery === "eu_entrego" ? (item.cutoff_entrega || cfg.horario_limite_entrega) : (item.cutoff_retirada || cfg.horario_limite_retirada)}</b> e já passou. Escolha a partir de amanhã para garantir o tempo de preparação do locador.
                  </div>
                )}

                {/* V18.9.13 — avisa ANTES de preencher tudo que é preciso estar logado
                    (antes o usuário só descobria ao salvar o endereço: "Não autenticado"). */}
                {!user && (
                  <div className="bg-[#004E98]/5 border border-[#004E98]/25 rounded-xl p-3 flex flex-col gap-2" data-testid="booking-login-notice">
                    <div className="text-xs text-slate-700 leading-relaxed">
                      Você está navegando <b>sem entrar</b>. Pode simular datas e valores à vontade, mas para
                      salvar endereço e concluir o aluguel é preciso entrar na sua conta.
                    </div>
                    <button
                      type="button"
                      onClick={() => requireAuth(() => {})}
                      className="btn-primary text-xs py-2 px-4 self-start"
                      data-testid="booking-login-btn"
                    >
                      Entrar ou criar conta
                    </button>
                  </div>
                )}

                {/* V8.5.2: Calendário ÚNICO com seleção de RANGE (De - Até) — Airbnb-style */}
                <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="range-calendar-wrapper">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Período do aluguel</div>
                    {blockedDates.length > 0 && (
                      <div className="text-[10px] text-slate-500 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
                        {blockedDates.length} dia(s) ocupado(s)
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">De</div>
                      <div className="font-bold text-sm text-slate-900" data-testid="range-from-display">
                        {start ? formatDateBR(start) : "Selecione"}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Até</div>
                      <div className="font-bold text-sm text-slate-900" data-testid="range-to-display">
                        {end ? formatDateBR(end) : (start ? "Toque no mesmo dia ou outro" : "Selecione")}
                      </div>
                    </div>
                  </div>
                  {isClosed && (
                    <div className="mb-3 rounded-xl border-2 border-[#004E98]/20 bg-[#004E98]/[0.03] p-3" data-testid="closed-blocks-picker">
                      <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">
                        Blocos de {cycleDays} dia(s)
                      </div>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => setBlocks((b) => Math.max(1, b - 1))} className="w-9 h-9 rounded-lg border-2 border-slate-200 font-bold text-lg leading-none disabled:opacity-40" disabled={blocks <= 1} data-testid="blocks-minus">−</button>
                        <div className="text-center min-w-[64px]">
                          <div className="font-heading font-black text-xl text-[#004E98]" data-testid="blocks-count">{blocks}</div>
                          <div className="text-[10px] text-slate-500">= {blocks * cycleDays} dias</div>
                        </div>
                        <button type="button" onClick={() => setBlocks((b) => b + 1)} className="w-9 h-9 rounded-lg border-2 border-slate-200 font-bold text-lg leading-none" data-testid="blocks-plus">+</button>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1.5">Escolha o início no calendário — a devolução é definida automaticamente.</div>
                    </div>
                  )}
                  {isFlexible && (
                    <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800" data-testid="flexible-min-note">
                      Período mínimo: <b>{minDaysFlex} dia(s)</b>. Depois você pode estender dia a dia.
                    </div>
                  )}
                  <ShadcnCalendar
                    mode="range"
                    numberOfMonths={1}
                    locale={ptBR}
                    formatters={{
                      formatWeekdayName: (date) => ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][date.getDay()],
                    }}
                    classNames={{
                      day_today: "ring-1 ring-[#004E98]/40 text-[#004E98] font-bold rounded-md",
                      day_selected: "!bg-[#004E98] !text-white hover:!bg-[#004E98] hover:!text-white focus:!bg-[#004E98] focus:!text-white",
                      day_range_start: "!bg-[#004E98] !text-white rounded-l-md",
                      day_range_end: "!bg-[#004E98] !text-white rounded-r-md",
                      day_range_middle: "!bg-[#004E98]/15 !text-[#004E98] rounded-none",
                    }}
                    selected={{
                      from: start ? new Date(start + "T12:00:00") : undefined,
                      to: end ? new Date(end + "T12:00:00") : undefined,
                    }}
                    onSelect={() => {}}
                    onDayClick={(day) => {
                      const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
                      handleDayPick(iso);
                    }}
                    disabled={(d) => {
                      const iso = d.toISOString().split("T")[0];
                      if (iso < minStartDate) return true;
                      if (blockedDates.includes(iso)) return true;
                      if (Array.isArray(item?.dias_operacao) && item.dias_operacao.length > 0 && item.dias_operacao.length < 7) {
                        if (!item.dias_operacao.includes(d.getDay())) return true;
                      }
                      return false;
                    }}
                    modifiers={{
                      booked: blockedDates.map((iso) => new Date(iso + "T12:00:00")),
                    }}
                    modifiersClassNames={{
                      booked: "bg-rose-100 text-rose-600 line-through font-normal hover:bg-rose-100 cursor-not-allowed",
                    }}
                    className="rounded-md mx-auto"
                    data-testid="range-calendar"
                  />
                  <div className="flex flex-wrap items-center gap-3 mt-2 px-1 text-[10px] text-slate-500">
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-rose-100 border border-rose-300" /> <b className="text-rose-600">Reservado</b></span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-slate-100 border border-slate-300 line-through" /> <b className="text-slate-500">Indisponível</b></span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#004E98]" /> <b className="text-[#004E98]">Período selecionado</b></span>
                  </div>
                  {showDurationStepper && (
                    <div className="mt-3" data-testid="duration-stepper">
                      <div className="text-[11px] font-bold text-slate-500 mb-1.5">Duração (a partir do início):</div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => changeCycles(-1)}
                          disabled={curCycles <= 1}
                          className="w-9 h-9 rounded-full border-2 border-[#004E98] text-[#004E98] font-black text-lg flex items-center justify-center disabled:opacity-30"
                          data-testid="duration-minus"
                        >−</button>
                        <div className="min-w-[110px] text-center font-black text-[#004E98] text-sm" data-testid="duration-count">
                          {curSpanDays > 0 ? cycleBreakdownLabel(curSpanDays, cycleDays, item.cycle_unit) : `Escolha`}
                        </div>
                        <button
                          type="button"
                          onClick={() => changeCycles(1)}
                          className="w-9 h-9 rounded-full border-2 border-[#004E98] bg-[#004E98] text-white font-black text-lg flex items-center justify-center"
                          data-testid="duration-plus"
                        >+</button>
                      </div>
                      {showFineDays && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[11px] text-slate-500 font-semibold">Ajuste fino:</span>
                          <button
                            type="button"
                            onClick={() => changeDays(-fineStep)}
                            disabled={!curSpanDays || curSpanDays - fineStep < minDaysFlex}
                            className="px-3 py-1 rounded-full text-xs font-bold border-2 border-slate-200 text-slate-700 disabled:opacity-30"
                            data-testid="duration-minus-days"
                          >−{fineStep} dias</button>
                          <button
                            type="button"
                            onClick={() => changeDays(fineStep)}
                            className="px-3 py-1 rounded-full text-xs font-bold border-2 border-slate-200 text-slate-700 hover:border-[#004E98] hover:text-[#004E98]"
                            data-testid="duration-plus-days"
                          >+{fineStep} dias</button>
                          {curSpanDays > 0 && <span className="text-[11px] text-slate-400">({curSpanDays} dias)</span>}
                        </div>
                      )}
                    </div>
                  )}
                  {(start || end) && (
                    <button
                      onClick={() => { setStart(""); setEnd(""); }}
                      className="mt-2 text-[11px] text-slate-500 underline hover:text-slate-700"
                      data-testid="range-clear-btn"
                      type="button"
                    >
                      Limpar seleção
                    </button>
                  )}
                </div>
                {cutoffInfo?.same_day && cutoffInfo?.ok && cutoffInfo?.eta_iso && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs text-emerald-800" data-testid="cutoff-eta-notice">
                    <Clock size={12} weight="fill" className="inline mr-1" />
                    Previsão de {effectiveDelivery === "eu_entrego" ? "entrega" : "liberação"}: <b>{new Date(cutoffInfo.eta_iso).toLocaleString("pt-BR")}</b>
                    {" "}(+{cutoffInfo.prep_hours}h de preparação)
                  </div>
                )}

                {/* V7.7: Estoque dinâmico no período selecionado — sempre visível quando há datas */}
                {start && end && stockInfo && (
                  <div
                    className={`rounded-lg p-2 text-xs flex items-center gap-1.5 border ${stockInfo.disponivel === 0 ? "bg-red-50 border-red-200 text-red-800" : stockInfo.disponivel <= 2 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
                    data-testid="dynamic-stock-info"
                  >
                    <Package size={13} weight="bold" />
                    {stockInfo.disponivel === 0 ? (
                      <span><b>Sem estoque</b> nas datas selecionadas — escolha outro período.</span>
                    ) : stockInfo.disponivel === stockInfo.estoque_total ? (
                      <span><b>{stockInfo.disponivel} {stockInfo.disponivel === 1 ? "unidade disponível" : "unidades disponíveis"}</b> no período.</span>
                    ) : (
                      <span><b>Apenas {stockInfo.disponivel} {stockInfo.disponivel === 1 ? "unidade" : "unidades"}</b> {stockInfo.disponivel === 1 ? "disponível" : "disponíveis"} no período (de {stockInfo.estoque_total} no total).</span>
                    )}
                  </div>
                )}

                {/* V7.2/V17: Quantity selector com regras (mínimo + lote + calculadora) */}
                {(estoqueTotal > 1 || hasQtyRules) && (
                  <div data-testid="quantity-block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Quantidade (estoque: {estoqueTotal})</span>
                    {convActive && (
                      <div className="mt-1 rounded-xl border-2 border-[#004E98]/25 bg-[#004E98]/[0.04] p-2.5 space-y-1.5" data-testid="conversion-calculator">
                        <div className="text-[10px] font-black uppercase tracking-wider text-[#004E98] flex items-center gap-1">
                          <Calculator size={11} weight="fill" /> Calculadora de peças
                        </div>
                        <label className="block text-xs font-bold text-slate-800">
                          {qtyRules.conversion_question || `Quantos ${qtyRules.conversion_unit || "unidades"} você precisa?`}
                        </label>
                        <input
                          type="number" min="0" step="0.1" inputMode="decimal"
                          placeholder={`Digite a medida em ${qtyRules.conversion_unit || "unidades"}`}
                          value={convInput}
                          onChange={(e) => {
                            const v = e.target.value;
                            setConvInput(v);
                            const m = parseFloat(v);
                            if (!isNaN(m) && m > 0) setQuantity(snapQty(Math.ceil(m * convFactor)));
                          }}
                          className="aj-input"
                          data-testid="conversion-input"
                        />
                        {convInput && parseFloat(convInput) > 0 ? (
                          <div className="text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="conversion-result">
                            ✓ Você vai alugar <b>{quantity} peça(s)</b> {qtyRules.conversion_unit ? `para ${convInput} ${qtyRules.conversion_unit}${parseFloat(convInput) > 1 ? "s" : ""}` : ""}.
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-500">
                            {convFactor > 0 ? <>Cada {qtyRules.conversion_unit || "unidade"} = <b>{convFactor} peça(s)</b>. O app arredonda para cima.</> : "Preencha a medida acima para calcular."}
                          </div>
                        )}
                      </div>
                    )}
                    {(!convActive || !convRequired) && (() => {
                      const qtyMax = Math.max(minQty, parseInt(stockInfo?.disponivel ?? estoqueTotal, 10) || minQty);
                      const clampQ = (n) => Math.min(qtyMax, Math.max(minQty, n));
                      return (
                      <div className="flex items-center gap-3 mt-1.5">
                        <button
                          type="button"
                          onClick={() => { setConvInput(""); setQuantity((q) => clampQ((parseInt(q, 10) || minQty) - qtyStep)); }}
                          disabled={(parseInt(quantity, 10) || minQty) <= minQty}
                          className="w-9 h-9 rounded-lg border-2 border-slate-200 font-bold text-lg leading-none disabled:opacity-40"
                          data-testid="qty-minus"
                        >−</button>
                        <input
                          type="number"
                          min={minQty}
                          step={qtyStep}
                          max={qtyMax}
                          value={quantity === "" ? "" : quantity}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "") { setQuantity(""); return; }
                            const n = parseInt(v, 10);
                            if (!isNaN(n)) setQuantity(Math.min(qtyMax, Math.max(minQty, n)));
                          }}
                          onBlur={() => setQuantity(Math.min(qtyMax, snapQty(quantity)))}
                          className="aj-input text-center flex-1"
                          data-testid="booking-quantity"
                        />
                        <button
                          type="button"
                          onClick={() => { setConvInput(""); setQuantity((q) => Math.min(qtyMax, snapQty((parseInt(q, 10) || minQty) + qtyStep))); }}
                          disabled={(parseInt(quantity, 10) || minQty) >= qtyMax}
                          className="w-9 h-9 rounded-lg border-2 border-slate-200 font-bold text-lg leading-none disabled:opacity-40"
                          data-testid="qty-plus"
                        >+</button>
                      </div>
                      );
                    })()}
                    {convActive && convRequired && (
                      <div className="text-[11px] font-bold text-slate-700 mt-1" data-testid="qty-from-calculator">
                        Quantidade: <b>{quantity}</b> peça(s) — calculada automaticamente.
                      </div>
                    )}
                    {hasQtyRules && (minQty > 1 || qtyStep > 1) && (
                      <div className="text-[10px] text-slate-500 mt-1" data-testid="qty-rules-hint">
                        Mínimo: <b>{minQty}</b>{qtyStep > 1 ? <> · adicione de <b>{qtyStep} em {qtyStep}</b></> : null}
                      </div>
                    )}
                    {stockInfo && (
                      <div className={`text-[11px] mt-1 ${stockInfo.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="stock-info">
                        {stockInfo.ok
                          ? `✓ ${stockInfo.disponivel} disponíveis no período`
                          : `✗ Apenas ${stockInfo.disponivel} disponíveis no período (você pediu ${quantity})`}
                      </div>
                    )}
                    {/* V18.9 — Nudge de desconto por QUANTIDADE (visível junto do input de qty) */}
                    {pricing.quantity_nudge && (
                      <div className="mt-1.5 bg-[#004E98]/10 border border-[#004E98]/30 rounded-lg p-2 text-[11px] text-[#004E98] font-bold flex items-center gap-1.5" data-testid="quantity-discount-nudge">
                        <Lightning size={15} weight="fill" className="shrink-0" />
                        <span>Adicione mais <b>{pricing.quantity_nudge.qty_needed} {pricing.quantity_nudge.qty_needed === 1 ? "item" : "itens"}</b> e libere <b>{pricing.quantity_nudge.discount_pct}% de desconto</b> no valor total!</span>
                      </div>
                    )}
                  </div>
                )}

                {/* V17: Complementos — variantes, opcionais e consumíveis */}
                {comps.length > 0 && (
                  <div className="space-y-2.5" data-testid="complements-block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Monte seu aluguel</span>
                    {comps.map((c, i) => {
                      if (c.kind === "variant") {
                        // V18.3 — Ordena as opções: padrão SEMPRE em 1º lugar; demais mantêm a ordem original
                        const optsIndexed = (c.options || []).map((o, oi) => ({ o, oi }));
                        optsIndexed.sort((a, b) => {
                          if (!!a.o.is_default === !!b.o.is_default) return a.oi - b.oi;
                          return a.o.is_default ? -1 : 1;
                        });
                        // V18.3.1 — controles "abrir/fechar todos" só aparecem quando há
                        // pelo menos 1 opção com descrição preenchida
                        const opcoesComDescricao = optsIndexed.filter(({ o }) => !!(o.description && o.description.trim()));
                        const hasAnyDescription = opcoesComDescricao.length > 0;
                        const allOpen = hasAnyDescription && opcoesComDescricao.every(({ oi }) => variantDescOpen[`${i}:${oi}`]);
                        const setAllVariantDesc = (open) => {
                          setVariantDescOpen((m) => {
                            const next = { ...m };
                            opcoesComDescricao.forEach(({ oi }) => { next[`${i}:${oi}`] = open; });
                            return next;
                          });
                        };
                        return (
                          <div key={i} className="rounded-xl border-2 border-[#7C3AED]/20 bg-[#7C3AED]/[0.03] p-2.5" data-testid={`comp-variant-${i}`}>
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="text-xs font-black text-slate-800 flex-1 min-w-0">
                                {c.name} {c.required && <span className="text-[#FF6B35]">*</span>}
                                <span className="text-[9px] uppercase tracking-wider font-bold text-[#7C3AED] ml-1">escolha 1</span>
                              </div>
                              {hasAnyDescription && (
                                <button
                                  type="button"
                                  onClick={() => setAllVariantDesc(!allOpen)}
                                  className="shrink-0 text-[10px] font-bold text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded-full px-2 py-0.5 flex items-center gap-1 transition"
                                  data-testid={`comp-variant-${i}-toggle-all`}
                                >
                                  {allOpen ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
                                  {allOpen ? "Fechar todos" : "Abrir todos"}
                                </button>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              {optsIndexed.map(({ o, oi }) => {
                                const sel = selVariant[i] === oi;
                                const delta = parseFloat(o.price_delta || 0) || 0;
                                const isDefaultOpt = !!o.is_default;
                                const hasDescription = !!(o.description && o.description.trim());
                                const descKey = `${i}:${oi}`;
                                const descOpen = !!variantDescOpen[descKey];
                                const onSelectOption = () => {
                                  const willSelect = !(sel && !c.required);
                                  setSelVariant((m) => ({ ...m, [i]: willSelect ? oi : null }));
                                  // V18.3.1 — auto-abre a descrição ao selecionar (colapsada por padrão até então)
                                  if (willSelect && hasDescription) {
                                    setVariantDescOpen((m) => ({ ...m, [descKey]: true }));
                                  }
                                };
                                return (
                                  <div key={oi} className={`w-full rounded-lg border-2 text-left text-xs font-bold transition-colors ${sel ? "border-[#7C3AED] bg-white" : "border-slate-200 bg-white/60"}`} data-testid={`comp-${i}-opt-${oi}`}>
                                    <div className="flex items-center gap-2 p-2">
                                      {o.image ? (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); setCompLightboxImg(o.image); }}
                                          className="w-11 h-11 rounded-lg overflow-hidden border border-slate-200 shrink-0 hover:ring-2 hover:ring-[#7C3AED]/40 transition"
                                          aria-label="Ampliar foto"
                                          data-testid={`comp-${i}-opt-${oi}-thumb`}
                                        >
                                          <img src={fileUrl(o.image)} alt={o.label} className="w-full h-full object-cover" />
                                        </button>
                                      ) : (
                                        <div className="w-11 h-11 rounded-lg bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-slate-300">
                                          <Package size={16} weight="bold" />
                                        </div>
                                      )}
                                      <button type="button"
                                        onClick={onSelectOption}
                                        className="flex-1 min-w-0 flex items-start justify-between gap-2 text-left"
                                        data-testid={`comp-${i}-opt-${oi}-select`}>
                                        <span className="flex-1 min-w-0 flex flex-wrap items-center gap-1 leading-snug break-words">
                                          <span className="break-words">{o.label}</span>
                                          {isDefaultOpt && (
                                            <span className="text-[8px] uppercase font-black tracking-wider bg-[#7C3AED]/10 text-[#7C3AED] rounded-full px-1.5 py-0.5 whitespace-nowrap" data-testid={`comp-${i}-opt-${oi}-default-badge`}>
                                              valor base padrão
                                            </span>
                                          )}
                                        </span>
                                        <span className={`shrink-0 whitespace-nowrap ${isDefaultOpt ? "text-[#004E98]" : delta > 0 ? "text-[#FF6B35]" : "text-slate-400"}`}>
                                          {isDefaultOpt
                                            ? `${formatBRL(item.price_per_day)}/${cycleUnitSing}`
                                            : (delta === 0 ? "incluído" : `+${formatBRL(delta)}/${cycleUnitSing}`)}
                                        </span>
                                      </button>
                                      {hasDescription && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); setVariantDescOpen((m) => ({ ...m, [descKey]: !descOpen })); }}
                                          className="shrink-0 p-1 rounded-md text-[#7C3AED] hover:bg-[#7C3AED]/10 transition"
                                          aria-expanded={descOpen}
                                          aria-label={descOpen ? "Ocultar descrição" : "Mostrar descrição"}
                                          data-testid={`comp-${i}-opt-${oi}-desc-toggle`}
                                        >
                                          {descOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                                        </button>
                                      )}
                                    </div>
                                    {/* V18.3 — Descrição colapsável (auto-abre ao selecionar; usuário pode fechar/abrir manualmente) */}
                                    {hasDescription && descOpen && (
                                      <div className="px-3 pb-2.5 pt-0 text-[11px] text-slate-600 font-normal leading-snug border-t border-slate-100 break-words whitespace-pre-line" data-testid={`comp-${i}-opt-${oi}-description`}>
                                        {o.description}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      const isConsumable = c.kind === "consumable";
                      const price = parseFloat(c.price || 0) || 0;
                      const rawMaxq = Math.max(1, parseInt(c.max_qty || 1, 10) || 1);
                      // V17.5: cap por estoque disponível + esconde/desabilita se pausado
                      const av = compAvail[i] || {};
                      const availNum = av.unlimited || av.available == null ? Infinity : Math.max(0, parseInt(av.available, 10) || 0);
                      const effectiveMax = Math.min(rawMaxq, availNum === Infinity ? rawMaxq : availNum);
                      const isPaused = !!av.paused || !!c.paused;
                      const isSoldOut = !isPaused && effectiveMax <= 0;
                      const maxq = effectiveMax;
                      const q = Math.min(rawMaxq, addonQty(i));
                      const perCycle = !isConsumable && c.charge === "per_cycle";
                      // Ocultar totalmente se pausado (locador pausou); esgotado ainda aparece com aviso
                      if (isPaused) return null;
                      // V17.2+: obrigatoriedade — fixo ou proporcional
                      let requiredMin = 0;
                      if (c.kind === "addon" && c.require_active) {
                        const mode = c.require_mode || "proportional";
                        if (mode === "fixed") {
                          requiredMin = Math.min(maxq, Math.max(1, parseInt(c.require_min_fixed || 1, 10) || 1));
                        } else if (mode === "proportional") {
                          const per = Math.max(1, parseInt(c.require_per_base_qty || 1, 10) || 1);
                          const ratio = Math.max(1, parseInt(c.require_ratio || 1, 10) || 1);
                          const src = (c.require_per_source || "quantity");
                          const baseVal = src === "measure"
                            ? (parseFloat(convInput) || 0)
                            : (parseInt(quantity || 1, 10) || 1);
                          if (baseVal > 0) requiredMin = Math.min(maxq, Math.floor(baseVal / per) * ratio);
                        }
                      }
                      const belowMin = requiredMin > 0 && q < requiredMin;
                      // V18.8 — Descrição opcional para addon/consumable (colapsável, auto-abre ao adicionar)
                      const hasCompDescription = !!(c.description && String(c.description).trim());
                      const compDescOpen = !!addonDescOpen[i];
                      const compAccent = isConsumable ? "#FF6B35" : "#004E98";
                      return (
                        <div key={i} className={`rounded-xl border-2 ${belowMin ? "border-amber-400 bg-amber-50" : q > 0 ? (isConsumable ? "border-[#FF6B35]/50 bg-[#FF6B35]/[0.04]" : "border-[#004E98]/50 bg-[#004E98]/[0.04]") : "border-slate-200"}`} data-testid={`comp-addon-${i}`}>
                          <div className="p-2.5 flex items-center gap-2">
                          {c.image ? (
                            <button
                              type="button"
                              onClick={() => setCompLightboxImg(c.image)}
                              className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 shrink-0 hover:ring-2 hover:ring-[#004E98]/40 transition"
                              aria-label="Ampliar foto"
                              data-testid={`comp-addon-${i}-thumb`}
                            >
                              <img src={fileUrl(c.image)} alt={c.name} className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-slate-300">
                              <Package size={18} weight="bold" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-black text-slate-800 flex items-center gap-1 flex-wrap">
                              {c.name}
                              {c.group_key && (
                                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700" data-testid={`comp-addon-${i}-group`}>
                                  {c.group_key}: {c.group_mode === "exclusive" ? "escolha 1" : `máx ${c.group_max || 2}`}
                                </span>
                              )}
                              {isSoldOut && (
                                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600" data-testid={`comp-addon-${i}-soldout`}>
                                  Esgotado
                                </span>
                              )}
                              {!isSoldOut && !av.unlimited && av.available != null && av.available <= 3 && (
                                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`comp-addon-${i}-lowstock`}>
                                  Restam {av.available}
                                </span>
                              )}
                              {requiredMin > 0 && (
                                <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full ${belowMin ? "bg-amber-200 text-amber-900" : "bg-emerald-200 text-emerald-900"}`} data-testid={`comp-addon-${i}-required`}>
                                  Mín. {requiredMin}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {formatBRL(price)} {perCycle ? `/${cycleUnitSing}` : isConsumable ? "· venda (não retorna)" : "· por locação"}
                            </div>
                            {belowMin && (
                              <div className="text-[10px] font-bold text-amber-800 mt-0.5" data-testid={`comp-addon-${i}-below-min`}>
                                Obrigatório: pelo menos {requiredMin} unidade(s) para {c.require_per_source === "measure" ? "a medida escolhida" : "a quantidade escolhida"}.
                              </div>
                            )}
                          </div>
                          {isSoldOut ? (
                            <div className="text-[10px] font-bold text-slate-500 shrink-0" data-testid={`comp-addon-${i}-unavailable`}>
                              indisponível
                            </div>
                          ) : maxq > 1 || q > 0 || requiredMin > 0 ? (
                            <div className="flex items-center gap-2 shrink-0">
                              <button type="button" onClick={() => applyAddonPick(i, Math.max(requiredMin, q - 1))}
                                disabled={q <= requiredMin}
                                className="w-7 h-7 rounded-lg border-2 border-slate-200 font-bold leading-none disabled:opacity-30" data-testid={`comp-${i}-minus`}>−</button>
                              <span className="text-sm font-black min-w-[18px] text-center" data-testid={`comp-${i}-qty`}>{q}</span>
                              <button type="button" onClick={() => applyAddonPick(i, Math.min(maxq, q + 1))}
                                disabled={q >= maxq}
                                className="w-7 h-7 rounded-lg border-2 border-slate-200 font-bold leading-none disabled:opacity-30" data-testid={`comp-${i}-plus`}>+</button>
                            </div>
                          ) : (
                            <button type="button" onClick={() => applyAddonPick(i, 1)}
                              className="shrink-0 text-[11px] font-black text-[#004E98] border-2 border-[#004E98]/30 rounded-full px-3 py-1 hover:bg-[#004E98]/5" data-testid={`comp-${i}-add`}>
                              + Adicionar
                            </button>
                          )}
                          {hasCompDescription && (
                            <button
                              type="button"
                              onClick={() => setAddonDescOpen((m) => ({ ...m, [i]: !compDescOpen }))}
                              className="shrink-0 p-1 rounded-md hover:bg-slate-100 transition"
                              style={{ color: compAccent }}
                              aria-expanded={compDescOpen}
                              aria-label={compDescOpen ? "Ocultar descrição" : "Mostrar descrição"}
                              data-testid={`comp-addon-${i}-desc-toggle`}
                            >
                              {compDescOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                            </button>
                          )}
                          </div>
                          {/* V18.8 — Descrição colapsável (auto-abre ao adicionar; usuário pode fechar/abrir manualmente) */}
                          {hasCompDescription && compDescOpen && (
                            <div className="px-3 pb-2.5 pt-0 text-[11px] text-slate-600 font-normal leading-snug border-t border-slate-100 break-words whitespace-pre-line" data-testid={`comp-addon-${i}-description`}>
                              {c.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {isAmbos && (
                  <div data-testid="delivery-choice-block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                      Como você quer receber? <span className="text-[#FF6B35]">*</span>
                    </span>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button
                        type="button"
                        onClick={() => setDeliveryChoice("eu_entrego")}
                        className={`p-2.5 rounded-xl border-2 text-xs font-bold flex flex-col items-center gap-1 ${deliveryChoice === "eu_entrego" ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200"}`}
                        data-testid="choose-eu-entrego"
                      >
                        <Truck size={20} weight="bold" />
                        Receber em casa
                        {(() => {
                          const fc = item.frete_config;
                          const t = fc?.tipo;
                          if (t === "gratis") return <span className="text-[10px] text-emerald-600">Frete grátis</span>;
                          if (t === "combinar") return <span className="text-[10px] text-slate-500">Frete a combinar</span>;
                          if (t === "por_km") return <span className="text-[10px] text-slate-500">Frete por km</span>;
                          if (t === "fixo" && fc?.valor_fixo > 0) return <span className="text-[10px] text-slate-500">+{formatBRL(fc.valor_fixo)} frete</span>;
                          if (!fc && valorFreteListing > 0) return <span className="text-[10px] text-slate-500">+{formatBRL(valorFreteListing)} frete</span>;
                          return null;
                        })()}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryChoice("locatario_busca")}
                        className={`p-2.5 rounded-xl border-2 text-xs font-bold flex flex-col items-center gap-1 ${deliveryChoice === "locatario_busca" ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200"}`}
                        data-testid="choose-locatario-busca"
                      >
                        <House size={20} weight="bold" />
                        Buscar com o dono
                        <span className="text-[10px] text-slate-500">Sem frete</span>
                      </button>
                    </div>
                  </div>
                )}

                {useBlocks24h && start && end && (
                  <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2" data-testid="time-picker-block">
                    <div className="text-xs font-bold text-slate-700 flex items-center gap-1">
                      <Clock size={14} weight="bold" /> Horários
                      {ownerSchedule?.tz_label ? <span className="text-slate-400 font-normal">· atendimento do dono ({ownerSchedule.tz_label})</span> : null}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-[10px] uppercase font-bold text-slate-500">Retirada</span>
                        <select value={startTime} onChange={(e) => setStartTime(e.target.value)} className="aj-input mt-1" data-testid="pickup-time-select">
                          <option value="">Selecione</option>
                          {timeOptionsFor(start, effectiveDelivery === "eu_entrego" ? "delivery" : "pickup").map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-[10px] uppercase font-bold text-slate-500">Devolução</span>
                        <select value={endTime} onChange={(e) => { endManualRef.current = true; setEndTime(e.target.value); }} className="aj-input mt-1" data-testid="return-time-select">
                          <option value="">Selecione</option>
                          {timeOptionsFor(end, "pickup").map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </label>
                    </div>
                    {timeOptionsFor(start, effectiveDelivery === "eu_entrego" ? "delivery" : "pickup").length === 0 && (
                      <p className="text-[11px] text-amber-600" data-testid="pickup-day-closed">Não há mais horários disponíveis para retirada neste dia. Escolha outra data.</p>
                    )}
                    {timeOptionsFor(end, "pickup").length === 0 && (
                      <p className="text-[11px] text-amber-600" data-testid="return-day-closed">O dono não atende no dia de devolução escolhido. Escolha outra data.</p>
                    )}
                    {durationHours > 0 && (
                      <div className="text-[11px] text-slate-500" data-testid="duration-info">
                        Duração: <b>{durationHours}h</b> → cobra <b>{timeUnits} diária(s)</b> (1 diária = 24h).
                      </div>
                    )}
                    {fairCheckout && pricing.discount_amount === 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-[11px] text-emerald-800" data-testid="fair-checkout-banner">
                        💡 <b>Aproveite seu tempo:</b> você está pagando por {timeUnits} diária(s) (até {timeUnits * 24}h). Se quiser, pode estender a devolução até <b>{fairCheckout.maxDate.split("-").reverse().join("/")} às {fairCheckout.maxTime}</b> pelo mesmo valor — confirme a disponibilidade com o proprietário.
                      </div>
                    )}
                    {pricing.nudge && (
                      <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-lg p-2 text-[11px] text-[#FF6B35] font-bold flex items-center gap-1.5" data-testid="discount-nudge">
                        <Lightning size={15} weight="fill" className="shrink-0" />
                        {cycleMode ? (
                          <span>Dica AlugaJá: alugue por <b>{pricing.nudge.min_duration} {pricing.nudge.min_duration === 1 ? cycleUnitSing : unitLabelForItem(item)[1]}</b> e libere <b>{pricing.nudge.discount_pct}% de desconto</b> no valor total!</span>
                        ) : (
                          <span>Dica AlugaJá: adicione apenas mais <b>{pricing.nudge.units_needed} {pricing.nudge.units_needed === 1 ? "dia" : "dias"}</b> e libere <b>{pricing.nudge.discount_pct}% de desconto</b> no valor total!</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {periodReady && (!isAmbos || deliveryChoice) && (
                  <>
                    {/* Epic 2: endereço de entrega + frete dinâmico (quando o dono entrega) */}
                    {(effectiveDelivery === "eu_entrego") && (
                      <div className="border-t border-slate-100 pt-3" data-testid="delivery-address-block">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Endereço de entrega</span>
                        {addresses.length > 0 ? (
                          <select
                            value={selectedAddressId}
                            onChange={(e) => setSelectedAddressId(e.target.value)}
                            className="aj-input mt-1 text-sm"
                            data-testid="delivery-address-select"
                          >
                            {addresses.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.label ? `${a.label} — ` : ""}{a.street}{a.number ? `, ${a.number}` : ""} · {a.city}/{a.uf}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="mt-1 text-xs text-slate-500">Você ainda não tem endereços salvos.</div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const back = `/itens/${id}?resume=1&start=${start}&end=${end}&delivery=${deliveryChoice}&qty=${quantity}`;
                            requireAuth(() => nav(`/enderecos?returnTo=${encodeURIComponent(back)}`));
                          }}
                          className="mt-2 text-xs font-bold text-[#004E98] flex items-center gap-1.5"
                          data-testid="add-delivery-address-btn"
                        >
                          <Plus size={14} weight="bold" /> Novo endereço
                        </button>

                        {/* Status do frete calculado */}
                        {freteInfo.needs_geo ? (
                          <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800" data-testid="frete-needs-geo">
                            Este item cobra frete por km. Adicione a <b>localização (GPS)</b> ao endereço para calcular.{" "}
                            <button type="button" onClick={() => { const back = `/itens/${id}?resume=1&start=${start}&end=${end}&delivery=${deliveryChoice}&qty=${quantity}`; requireAuth(() => nav(`/enderecos?returnTo=${encodeURIComponent(back)}`)); }} className="underline font-bold">Completar endereço</button>
                          </div>
                        ) : freteInfo.a_combinar ? (
                          <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-600" data-testid="frete-a-combinar">
                            Frete <b>a combinar</b> com o dono (R$ 0,00 no app).
                          </div>
                        ) : (freteInfo.gratis || freteInfo.gratis_por_raio) ? (
                          <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-[11px] text-emerald-800" data-testid="frete-gratis">
                            Frete <b>grátis</b>{freteInfo.distance_km != null ? ` (${freteInfo.distance_km} km)` : ""}!
                          </div>
                        ) : valorFrete > 0 ? (
                          <div className="mt-2 text-[11px] text-slate-500" data-testid="frete-calc-info">
                            Frete: <b className="text-[#FF6B35]">{formatBRL(valorFrete)}</b>
                            {freteInfo.distance_km != null ? ` · ${freteInfo.distance_km} km${freteInfo.mult === 2 ? " (ida e volta)" : ""}` : ""}
                          </div>
                        ) : null}
                      </div>
                    )}

                    {/* V7.6: Nota livre para o locador quando o dono vai entregar */}
                    {(effectiveDelivery === "eu_entrego") && (
                      <label className="block" data-testid="delivery-notes-block">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                          Observação para a entrega (opcional)
                        </span>
                        <textarea
                          rows={2}
                          value={deliveryNotes}
                          onChange={(e) => setDeliveryNotes(e.target.value.slice(0, 500))}
                          placeholder="Ex: Entregar para o vizinho do 102, ligar antes de subir, portão azul..."
                          className="aj-input mt-1 resize-none text-sm"
                          data-testid="delivery-notes-input"
                        />
                        <span className="text-[10px] text-slate-400">
                          O locador verá essa nota junto com seu endereço após aceitar a reserva.
                        </span>
                      </label>
                    )}

                    {/* V17: Caução (garantia) — aviso transparente */}
                    {caucaoActive && (
                      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="caucao-notice">
                        <div className="text-xs font-black text-amber-900 flex items-center gap-1.5">
                          <Shield size={15} weight="fill" /> Este locador exige caução{caucaoAmount > 0 ? ` de ${formatBRL(caucaoAmount)}` : (item.caucao?.type === "percent" ? ` de ${item.caucao.value}% do valor da locação` : "")}
                        </div>
                        <p className="text-[11px] text-amber-900/90 leading-relaxed">
                          Este valor será apenas <b>retido (reservado) no seu cartão de crédito</b> — não é uma cobrança.
                          Ele é <b>estornado automaticamente ao fim da locação</b>, caso não haja divergências ou danos.
                        </p>
                        <p className="text-[10px] font-bold text-amber-800">
                          Por isso, este item aceita pagamento somente via cartão de crédito.
                        </p>
                      </div>
                    )}

                    {/* Payment method selector */}
                    <div className="border-t border-slate-100 pt-3" data-testid="payment-method-block">
                      <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Forma de pagamento</span>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => { setPaymentMethod("card"); setSplitGateway(null); }}
                          className={`p-2.5 rounded-xl border-2 text-xs font-bold flex flex-col items-center gap-1 ${paymentMethod === "card" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
                          data-testid="pay-method-card"
                        >
                          <CreditCard size={20} weight="bold" />
                          Cartão
                          
                        </button>
                        <button
                          type="button"
                          disabled={caucaoActive}
                          onClick={() => { setPaymentMethod("pix"); setSplitGateway(null); }}
                          className={`p-2.5 rounded-xl border-2 text-xs font-bold flex flex-col items-center gap-1 disabled:opacity-40 ${paymentMethod === "pix" ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`}
                          data-testid="pay-method-pix"
                        >
                          <QrCode size={20} weight="bold" />
                          PIX
                          {caucaoActive && <span className="text-[9px] text-slate-400 leading-tight">indisponível (caução)</span>}
                        </button>
                        <button
                          type="button"
                          disabled={caucaoActive}
                          onClick={() => user ? setPaymentMethod("wallet") : requireAuth(() => setPaymentMethod("wallet"))}
                          className={`p-2.5 rounded-xl border-2 text-xs font-bold flex flex-col items-center gap-1 disabled:opacity-40 ${paymentMethod === "wallet" ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200"}`}
                          data-testid="pay-method-wallet"
                        >
                          <Wallet size={20} weight="bold" />
                          Saldo
                          {caucaoActive ? (
                            <span className="text-[9px] text-slate-400 leading-tight">indisponível (caução)</span>
                          ) : (
                          <span className="text-[10px] text-slate-500" data-testid="wallet-available-label">
                            {walletBalance == null ? "—" : formatBRL(walletBalance)}
                          </span>
                          )}
                          {!caucaoActive && walletLocked > 0 && (
                            <span className="text-[9px] text-slate-400 leading-tight" data-testid="wallet-locked-hint">
                              +{formatBRL(walletLocked)} a liberar
                            </span>
                          )}
                        </button>
                      </div>
                      {/* V11.6: cartão salvo — pagar com 1 toque */}
                      {paymentMethod === "card" && savedCard && !splitActive && (
                        <div className="mt-2 space-y-2" data-testid="saved-card-block">
                          <button
                            type="button"
                            onClick={() => setUseOtherCard(false)}
                            className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-colors ${!useOtherCard ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
                            data-testid="use-saved-card"
                          >
                            <CreditCard size={18} weight="bold" className="text-[#004E98] shrink-0" />
                            <div className="flex-1">
                              <div className="text-sm font-bold">{savedCard.brand || "Cartão"} •••• {savedCard.last4}</div>
                              <div className="text-[11px] text-slate-500">Pague com 1 toque</div>
                            </div>
                            {!useOtherCard && <CheckCircle size={18} weight="fill" className="text-[#004E98] shrink-0" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => setUseOtherCard(true)}
                            className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-colors ${useOtherCard ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
                            data-testid="use-other-card"
                          >
                            <CreditCard size={18} weight="bold" className="text-slate-400 shrink-0" />
                            <div className="flex-1 text-sm font-bold">Usar outro cartão</div>
                            {useOtherCard && <CheckCircle size={18} weight="fill" className="text-[#004E98] shrink-0" />}
                          </button>
                          <button type="button" onClick={forgetCard} className="text-[11px] text-red-500 hover:underline inline-flex items-center gap-1" data-testid="forget-card">
                            <Trash size={11} weight="bold" /> Remover cartão salvo
                          </button>
                        </div>
                      )}
                      {/* V9.8: split — saldo não cobre o total, mas há algum saldo → completar com gateway */}
                      {paymentMethod === "wallet" && splitEligible && (
                        <div className="mt-2 space-y-2" data-testid="split-pay-block">
                          <div className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            <Warning size={12} weight="fill" className="inline mr-1 text-amber-600" />
                            Seu saldo de <b>{formatBRL(walletBalance)}</b> não cobre o total. Use o saldo e complete o restante:
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setSplitGateway("card")}
                              className={`p-2 rounded-xl border-2 text-xs font-bold flex items-center justify-center gap-1 ${splitGateway === "card" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
                              data-testid="split-method-card"
                            >
                              <CreditCard size={16} weight="bold" /> Saldo + Cartão
                            </button>
                            <button
                              type="button"
                              onClick={() => setSplitGateway("pix")}
                              className={`p-2 rounded-xl border-2 text-xs font-bold flex items-center justify-center gap-1 ${splitGateway === "pix" ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`}
                              data-testid="split-method-pix"
                            >
                              <QrCode size={16} weight="bold" /> Saldo + PIX
                            </button>
                          </div>
                          {splitActive && (
                            <div className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-0.5" data-testid="split-breakdown">
                              <div className="flex justify-between"><span>Pago com saldo</span><b data-testid="split-wallet-part">{formatBRL(walletPart)}</b></div>
                              <div className="flex justify-between"><span>Restante no {splitGateway === "card" ? "Cartão" : "PIX"}</span><b data-testid="split-gateway-part">{formatBRL(gatewayPart)}</b></div>
                              {splitGateway === "card" && (
                                <div className="text-[10px] text-slate-400 pt-0.5">A parte do cartão fica reservada e só é cobrada na retirada.</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {paymentMethod === "wallet" && walletShort && !splitEligible && (
                        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="wallet-insufficient-warn">
                          <Warning size={12} weight="fill" className="inline mr-1" />
                          Você não tem saldo disponível. Escolha <b>Cartão</b> ou <b>PIX</b>, ou recarregue créditos.
                        </div>
                      )}
                      {paymentMethod === "wallet" && !walletShort && walletBalance != null && (
                        <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="wallet-pay-ok">
                          <CreditCard size={11} weight="fill" className="inline mr-1" />
                          Pagando com saldo da Carteira — <b>sem taxa de cartão/PIX</b>.
                        </div>
                      )}
                    </div>

                    <div className="border-t border-slate-100 pt-3 space-y-1.5 text-sm" data-testid="checkout-breakdown">
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          {cycleMode
                            ? `${formatBRL(effPrice)} /${cycleUnitSing} · ${cycleSpanLabel}${quantity > 1 ? ` × ${quantity}un` : ""}`
                            : `${formatBRL(effPrice)} × ${timeUnits} ${unitLabel}${quantity > 1 ? ` × ${quantity}un` : ""}`}
                        </span>
                        {pricing.discount_amount > 0 ? (
                          <span className="font-semibold text-slate-400 line-through" data-testid="diarias-gross">{formatBRL(valorDiariasGross)}</span>
                        ) : (
                          <span className="font-semibold">{formatBRL(valorDiarias)}</span>
                        )}
                      </div>
                      {pricing.discount_amount > 0 && (
                        <div className="flex justify-between items-center bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="discount-line">
                          <span className="text-emerald-800 font-bold text-[12px] flex items-center gap-1">
                            <Confetti size={14} weight="fill" /> {pricing.discount_label}
                          </span>
                          <span className="font-black text-emerald-700" data-testid="discount-amount">- {formatBRL(pricing.discount_amount)}</span>
                        </div>
                      )}
                      {pricing.discount_amount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Subtotal com desconto</span>
                          <span className="font-semibold text-emerald-700" data-testid="diarias-net">{formatBRL(valorDiarias)}</span>
                        </div>
                      )}
                      {/* V17: complementos escolhidos */}
                      {comps.map((c, i) => {
                        if (c.kind === "variant" && selVariant[i] != null) {
                          const o = c.options?.[selVariant[i]];
                          return (
                            <div key={`v${i}`} className="flex justify-between text-[12px]" data-testid={`breakdown-variant-${i}`}>
                              <span className="text-slate-500">· {c.name}: <b>{o?.label}</b></span>
                              <span className="text-slate-400">no valor acima</span>
                            </div>
                          );
                        }
                        const q = addonQty(i);
                        if (q <= 0 || c.kind === "variant") return null;
                        const price = parseFloat(c.price || 0) || 0;
                        const perCycle = c.kind === "addon" && c.charge === "per_cycle";
                        const lineTotal = perCycle
                          ? Math.round(price * q * (compCycleUnits > 0 ? compCycleUnits : 0) * 100) / 100
                          : Math.round(price * q * 100) / 100;
                        return (
                          <div key={`a${i}`} className="flex justify-between text-[12px]" data-testid={`breakdown-addon-${i}`}>
                            <span className="text-slate-500">· {c.name} × {q}{perCycle ? ` (/${cycleUnitSing})` : ""}</span>
                            <span className="font-semibold">{formatBRL(lineTotal)}</span>
                          </div>
                        );
                      })}
                      {valorFrete > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Frete</span>
                          <span className="font-semibold">{formatBRL(valorFrete)}</span>
                        </div>
                      )}
                      {comboDiscountValue > 0 && (
                        <div className="flex justify-between items-center bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="combo-discount-line">
                          <span className="text-emerald-800 font-bold text-[12px] flex items-center gap-1">
                            <Confetti size={14} weight="fill" /> Combo aplicado ({comboDiscountPct}% OFF)
                          </span>
                          <span className="font-black text-emerald-700" data-testid="combo-discount-amount">- {formatBRL(comboDiscountValue)}</span>
                        </div>
                      )}
                      {/* V18.0 — Linhas do kit (uma cobrança única) com preço original riscado e desconto por combo */}
                      {kitQuote && !kitQuote.error && (kitQuote.combos || []).map((c) => {
                        const hasDiscount = c.combo_discount_pct && c.combo_discount_pct > 0;
                        return (
                          <div key={c.listing_id} className="flex justify-between items-center text-[12px]" data-testid={`checkout-kit-line-${c.listing_id}`}>
                            <span className="text-slate-600 truncate max-w-[60%]">
                              + <b>{c.title}</b>
                              {hasDiscount && <span className="ml-1 text-emerald-700 font-bold">-{parseFloat(c.combo_discount_pct).toFixed(0)}%</span>}
                            </span>
                            <span className="flex items-baseline gap-1.5">
                              {hasDiscount && (
                                <span className="text-slate-400 line-through text-[11px]" data-testid={`checkout-kit-gross-${c.listing_id}`}>
                                  {formatBRL(c.valor_diarias_pre_combo)}
                                </span>
                              )}
                              <span className="font-semibold text-slate-900" data-testid={`checkout-kit-net-${c.listing_id}`}>
                                {formatBRL(c.valor_diarias)}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                      {/* V18.0 — Banner "Você economiza" (soma desconto do combo do principal + descontos por combo do kit) */}
                      {(() => {
                        const kitSavings = kitQuote && !kitQuote.error
                          ? (kitQuote.combos || []).reduce((sum, c) => sum + Math.max(0, (parseFloat(c.valor_diarias_pre_combo) || 0) - (parseFloat(c.valor_diarias) || 0)), 0)
                          : 0;
                        const totalSavings = comboDiscountValue + kitSavings;
                        if (totalSavings < 0.01) return null;
                        return (
                          <div className="flex justify-between items-center bg-gradient-to-r from-emerald-50 to-emerald-100 border-2 border-emerald-300 rounded-xl px-3 py-2 mt-1" data-testid="combo-savings-banner">
                            <span className="text-emerald-900 font-black text-[12px] flex items-center gap-1.5">
                              <Confetti size={14} weight="fill" className="text-emerald-600" />
                              Levando o combo, você economiza
                            </span>
                            <span className="font-black text-emerald-700 text-sm" data-testid="combo-savings-amount">
                              {formatBRL(totalSavings)}
                            </span>
                          </div>
                        );
                      })()}
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          {coversWithWallet ? "Taxa AlugaJá (Saldo)" : "Taxa AlugaJá"}
                        </span>
                        <span className="font-semibold" data-testid="service-fee-value">{formatBRL(taxaServico)}</span>
                      </div>
                      {coversWithWallet ? (
                        <div className="text-[10px] text-emerald-700 -mt-0.5 pl-1" data-testid="service-fee-breakdown">
                          Sem taxa ao pagar com saldo
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-400 -mt-0.5 pl-1" data-testid="service-fee-breakdown">
                          Taxa fixa de uso da plataforma (R$ 0,50)
                        </div>
                      )}
                      <div className="flex justify-between font-heading text-lg font-black border-t border-slate-100 pt-2 mt-2">
                        <span>Total</span>
                        <span className="text-[#FF6B35]" data-testid="total-value">{formatBRL(total)}</span>
                      </div>
                      {caucaoActive && caucaoAmount > 0 && (
                        <div className="flex justify-between text-[12px] bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="breakdown-caucao">
                          <span className="text-amber-900 font-bold">+ Caução (apenas reservada no cartão)</span>
                          <span className="font-black text-amber-900">{formatBRL(caucaoAmount)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3 mt-1" data-testid="protected-payment-seal">
                      <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
                        <Shield size={16} weight="fill" className="text-white" />
                      </div>
                      <div className="text-[11px] leading-relaxed text-emerald-900">
                        <div className="font-black text-xs flex items-center gap-1">Compra Protegida AlugaJá</div>
                        <span>
                          O valor fica <b>reservado/retido</b> e só é liberado ao locador <b>quando você confirma a retirada com o código</b>. Pagamento seguro.
                          <Link to="/termos" target="_blank" className="text-emerald-800 underline ml-1">Política</Link>
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {periodReady && useBlocks24h && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[12px] text-blue-900 leading-relaxed flex gap-2" data-testid="booking-humanized-summary">
                    <Calendar size={16} weight="bold" className="text-blue-600 mt-0.5 shrink-0" />
                    <span>
                      Sua reserva será solicitada para <b>{effectiveDelivery === "eu_entrego" ? "entrega" : "retirada"}</b> em <b>{formatDateBR(start)} às {startTime}</b> e <b>devolução</b> em <b>{formatDateBR(end)} às {endTime}</b>.
                    </span>
                  </div>
                )}

                {listingBlockedBy && (
                  <div className="rounded-xl border-2 border-red-300 bg-red-50 p-2.5 text-[11px] font-bold text-red-800" data-testid="listing-blocked-banner">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Warning size={13} weight="fill" /> Item indisponível nas datas
                    </div>
                    <div className="text-[10px] font-normal text-red-700">
                      O opcional <b>{listingBlockedBy}</b> é obrigatório e está esgotado ou pausado nesse período. Tente outras datas.
                    </div>
                  </div>
                )}
                {simulateMode ? (
                  <button
                    disabled
                    className="btn-primary w-full opacity-60 cursor-not-allowed"
                    data-testid="simulate-book-disabled"
                    title="Modo simulação — pagamento desabilitado"
                  >
                    <Shield size={16} weight="bold" />
                    {`Pagar ${periodReady ? formatBRL(total) : "agora"}`}
                  </button>
                ) : (
                <button
                  onClick={book}
                  disabled={loading || (isAmbos && !deliveryChoice) || !periodReady || walletInsufficient || !!listingBlockedBy}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="book-btn"
                >
                  <Shield size={16} weight="bold" />
                  {loading ? "Redirecionando..." : listingBlockedBy ? `Sem estoque de ${listingBlockedBy}` : (() => {
                    if (effectivePM === "wallet") return `Usar saldo ${periodReady ? formatBRL(total) : ""}`;
                    if (splitActive) return `Pagar com saldo + ${splitGateway === "card" ? "cartão" : "PIX"}`;
                    return `Pagar ${periodReady ? formatBRL(total) : "agora"}`;
                  })()}
                </button>
                )}
                {simulateMode && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 leading-relaxed" data-testid="simulate-hint">
                    🧪 <b>Modo simulação</b> — o pagamento está desabilitado. Tudo o mais é exatamente o que o cliente vê.
                  </div>
                )}
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Endereço completo do dono é revelado apenas após a aceitação.
                </p>
              </>
            )}
          </div>
        </aside>
      </div>

      {/* V9.1: Modal de pagamento via Cartão — formulário Asaas (pré-autorização/escrow, sem redirect) */}
      {!simulateMode && showCardForm && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center pb-safe pt-safe" onClick={(e) => { if (e.target === e.currentTarget) { setShowCardForm(false); setCardError(""); } }} data-testid="card-checkout-modal">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl flex flex-col max-h-[92dvh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 pb-3 border-b border-slate-100 shrink-0">
              <div>
                <div className="font-heading font-black text-lg">Pagamento com Cartão</div>
                <div className="text-xs text-slate-500">Pagamento seguro · valor reservado até a retirada</div>
              </div>
              <button onClick={() => { setShowCardForm(false); setCardError(""); }} className="text-slate-500" data-testid="card-checkout-close">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="card-checkout-body" data-scroll-lock-allow="true">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2 text-xs text-emerald-900 space-y-1">
                <div className="flex justify-between"><span>Valor total do aluguel:</span><b>{formatBRL(total)}</b></div>
                {splitActive && (
                  <>
                    <div className="flex justify-between text-emerald-800"><span>Pago com saldo:</span><b>- {formatBRL(walletPart)}</b></div>
                    <div className="flex justify-between border-t border-emerald-200 pt-1"><span>Cobrar no cartão:</span><b data-testid="card-split-amount">{formatBRL(gatewayPart)}</b></div>
                  </>
                )}
              </div>
              {(loading || cardError || cardSuccess) ? (
                <PaymentProgress
                  active={loading && !cardError && !cardSuccess}
                  method="card"
                  error={cardError}
                  success={cardSuccess}
                />
              ) : null}
              {!loading && !cardSuccess && (
                <AsaasCardForm
                  amount={splitActive ? gatewayPart : total}
                  submitting={loading}
                  showInstallments={!splitActive}
                  holderCpf={user?.cpf}
                  onSubmit={async (cardData) => {
                    const result = await doCheckout(cardData);
                    if (result === "pending") {
                      setShowCardForm(false);
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* V17.9.3 — Modal do simulador (dono) */}
      {simulateResult && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={(e) => e.target === e.currentTarget && setSimulateResult(null)} data-testid="simulate-result-modal">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[92dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-black text-lg text-amber-800">🧪 Detalhamento (simulação)</h3>
              <button onClick={() => setSimulateResult(null)} className="text-slate-400"><X size={20} weight="bold" /></button>
            </div>
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[11px] text-emerald-800 uppercase font-black tracking-wider">Cliente paga</div>
              <div className="font-heading font-black text-3xl text-emerald-900" data-testid="sim-total-client">{formatBRL(simulateResult.total)}</div>
            </div>
            <div className="rounded-xl border-2 border-[#004E98]/30 bg-[#004E98]/[0.06] p-3">
              <div className="text-[11px] text-[#004E98] uppercase font-black tracking-wider">Você (dono) recebe</div>
              <div className="font-heading font-black text-3xl text-[#004E98]" data-testid="sim-owner-net">{formatBRL(simulateResult.ownerNet)}</div>
            </div>
            <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-3 text-[12px] space-y-1">
              <div className="flex justify-between"><span className="text-slate-600">Diárias ({simulateResult.days}×)</span><span className="font-bold">{formatBRL(simulateResult.valorDiarias)}</span></div>
              {simulateResult.complementsTotal > 0 && (<div className="flex justify-between"><span className="text-slate-600">Opcionais/consumíveis</span><span className="font-bold">{formatBRL(simulateResult.complementsTotal)}</span></div>)}
              {simulateResult.valorFrete > 0 && (<div className="flex justify-between"><span className="text-slate-600">Frete</span><span className="font-bold">{formatBRL(simulateResult.valorFrete)}</span></div>)}
              {simulateResult.comboDiscountValue > 0 && (<div className="flex justify-between text-emerald-700"><span>Desconto combo</span><span className="font-bold">− {formatBRL(simulateResult.comboDiscountValue)}</span></div>)}
              <div className="flex justify-between"><span className="text-slate-600">Taxa AlugaJá (cliente)</span><span className="font-bold">{formatBRL(simulateResult.taxaServico)}</span></div>
              <div className="border-t border-slate-200 my-1" />
              <div className="text-[10px] text-slate-500 font-black uppercase tracking-wider">Deduções do dono</div>
              <div className="flex justify-between"><span className="text-slate-600">Comissão AlugaJá (10% ou piso)</span><span className="font-bold">− {formatBRL(simulateResult.commission)}</span></div>
              {simulateResult.caucaoActive && (
                <div className="flex justify-between text-amber-800"><span>Taxa gerenciamento caução</span><span className="font-bold">− {formatBRL(simulateResult.caucaoFee)}</span></div>
              )}
              {simulateResult.caucaoActive && (
                <div className="mt-2 text-[10px] rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-900">
                  🛡 Caução de <b>{formatBRL(simulateResult.caucaoAmount)}</b> será reservada no cartão do cliente (sem cobrança). Se não houver problema, é liberada automaticamente. A taxa de R$ 5,00 é descontada em toda locação com caução — pelo serviço de gerenciamento.
                </div>
              )}
            </div>
            <button onClick={() => setSimulateResult(null)} className="btn-ghost w-full py-2" data-testid="sim-close">Fechar</button>
          </div>
        </div>,
        document.body
      )}

      {/* V8.1: Modal QR PIX + polling de confirmação */}
      {!simulateMode && pixCheckout && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center pb-safe pt-safe" data-testid="pix-checkout-modal">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[92dvh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-white z-10 -mt-1 pt-1 pb-2">
              <div>
                <div className="font-heading font-black text-lg">Pague com PIX</div>
                <div className="text-xs text-slate-500">Aguardando confirmação automática</div>
              </div>
              <button onClick={() => { setPixCheckout(null); setCurrentBookingId(null); nav("/minhas-reservas"); }} className="text-slate-500" data-testid="pix-checkout-close">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 text-center">
              <div className="text-xs text-emerald-800">Total</div>
              <div className="font-heading font-black text-3xl text-emerald-900" data-testid="pix-total">{formatBRL(pixCheckout.total_charged || total)}</div>
            </div>
            {pixCheckout.qr_code_base64 && (
              <div className="flex justify-center bg-white p-3 rounded-xl border border-slate-200">
                <img src={`data:image/png;base64,${pixCheckout.qr_code_base64}`} alt="QR PIX" className="w-56 h-56" data-testid="pix-qr-img" />
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Código copia-e-cola</div>
              <div className="flex gap-2">
                <input readOnly value={pixCheckout.qr_code || ""} className="aj-input text-[10px] flex-1 font-mono" data-testid="pix-copy-paste" />
                <button onClick={copyPixCode} className="btn-primary px-3" data-testid="pix-copy-btn"><Copy size={16} weight="bold" /></button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-900">
              <Warning size={12} weight="fill" className="inline mr-1" />
              Verificando pagamento automaticamente ({pixPolls}/30). Você pode fechar — vamos notificar por e-mail e WhatsApp quando confirmar.
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
